import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import * as api from "../api/client";
import type {
  AdminCardDetail,
  AdminCardInfo,
  AdminJob,
  AdminJobCardCost,
  AdminJobStageTimings,
  AdminJobStatus,
  AdminJobUsage,
  AdminPool,
  CatalogCard,
  GenerateCardType,
  GenerateFaction,
  GenerateRarity,
} from "../api/client";
import type { CardView } from "../api/types";
import { AppHeader, Breadcrumb } from "../components/AppHeader";
import { Card, naturalCardWidthPx } from "../components/Card";
import {
  CardBrowser,
  CardBrowserSelect,
  type CardBrowserFilters,
} from "../components/CardBrowser";
import { Inspector } from "../components/Inspector";
import { ItemSprite } from "../components/ItemSprite";
import { AdminNpcDecksTab } from "../components/admin/AdminNpcDecksTab";
import { BalanceTab } from "../components/admin/BalanceTab";
import { DeckBalanceTab } from "../components/admin/DeckBalanceTab";
import { StatsTab } from "../components/admin/StatsTab";
import { AnimatedEllipsis } from "../components/admin/AnimatedEllipsis";
import { ModelCompareTool } from "../components/admin/ModelCompareTool";
import { PromptCompareTool } from "../components/admin/PromptCompareTool";
import { ValidatorTestTool } from "../components/admin/ValidatorTestTool";
import { PaletteSnapTool } from "../components/admin/PaletteSnapTool";
import { ImageCreatorTool } from "../components/admin/ImageCreatorTool";
import { BuildDeckAi } from "../components/admin/BuildDeckAi";
import { CardEditorPage } from "./CardEditorPage";
import { GenerateSettingsBar } from "../components/admin/GenerateSettingsBar";
import { formatVoidJuice } from "../logic/currency";
import { stringCodec, useUrlSyncedState } from "../logic/useFilterState";
import { useResponsiveLayout } from "../logic/useResponsiveLayout";
import { useMe } from "../api/useMe";
import { playSfx } from "../logic/sfx";
import { pixelTheme } from "../theme/pixel";
import { E32 } from "../theme/endesga32";
import { bevelShadow, defaultBtn } from "../theme/styles";

type Tab = "cards" | "generate" | "manage" | "tools" | "npcs" | "stats";
export type AdminCardSort = "cost" | "created";

export function AdminPage() {
  const { tab: tabParam } = useParams<{ tab?: string }>();
  // /admin/players redirects below; treat as manage so the bookmark works.
  // /admin/playtest and /admin/balance moved into the Tools tab as
  // sub-tools, so their legacy URLs route to the Tools tab. Sub-tool
  // selection inside Tools is handled by `?tool=` (see AdminToolsTab).
  const tab: Tab =
    tabParam === "generate"
      ? "generate"
      : tabParam === "manage" || tabParam === "players"
        ? "manage"
        : tabParam === "tools" ||
            tabParam === "playtest" ||
            tabParam === "balance"
          ? "tools"
          : tabParam === "npcs"
            ? "npcs"
            : tabParam === "stats"
              ? "stats"
              : "cards";
  const navigate = useNavigate();
  const theme = pixelTheme;
  // Auth is enforced by `RequireAuth` in App.tsx — by the time this
  // page renders, `useMe()` is guaranteed to return a non-null admin.
  // The non-null assertion below is structural, not a fallback.
  const user = useMe();
  if (!user) return null;

  return (
    <div
      style={{
        height: "100%",
        overflow: "hidden",
        background: theme.pageBg,
        color: theme.ink,
        fontFamily: theme.fontBody,
        padding: "20px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <AppHeader user={user} />
      <Breadcrumb text={`ADMIN · ${tab.toUpperCase()}`} />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <TabButton
          theme={theme}
          active={tab === "cards"}
          onClick={() => navigate("/admin/cards")}
        >
          CARDS
        </TabButton>
        <TabButton
          theme={theme}
          active={tab === "generate"}
          onClick={() => navigate("/admin/generate")}
        >
          GENERATE
        </TabButton>
        <TabButton
          theme={theme}
          active={tab === "manage"}
          onClick={() => navigate("/admin/manage")}
        >
          MANAGE
        </TabButton>
        <TabButton
          theme={theme}
          active={tab === "tools"}
          onClick={() => navigate("/admin/tools")}
        >
          TOOLS
        </TabButton>
        <TabButton
          theme={theme}
          active={tab === "npcs"}
          onClick={() => navigate("/admin/npcs")}
        >
          NPCS
        </TabButton>
        <TabButton
          theme={theme}
          active={tab === "stats"}
          onClick={() => navigate("/admin/stats")}
        >
          STATS
        </TabButton>
      </div>

      {tab === "cards" ? (
        <AdminCardsTab />
      ) : tab === "generate" ? (
        <AdminGenerateTab />
      ) : tab === "tools" ? (
        <AdminToolsTab initialTool={tabParam} />
      ) : tab === "npcs" ? (
        <AdminNpcDecksTab />
      ) : tab === "stats" ? (
        <StatsTab />
      ) : (
        <AdminManageTab />
      )}
    </div>
  );
}

// AdminCardInfo → minimal CardView for Card. `imageVersion` is
// bumped after regen-image to cache-bust the PNG.
function infoToCardView(info: AdminCardInfo, imageVersion = 0): CardView {
  const attack = info.attack ?? 0;
  const life = info.life ?? 0;
  const rawUrl = info.image_url ? String(info.image_url) : null;
  const url =
    rawUrl && imageVersion > 0 ? `${rawUrl}?v=${imageVersion}` : rawUrl;
  return {
    entity_id: 0,
    def_id: info.id,
    name: info.name ?? info.id,
    type: (info.type ?? "creature") as CardView["type"],
    cost: info.cost ?? 0,
    base_attack: attack,
    base_life: life,
    current_attack: attack,
    current_life: life,
    damage_marked: 0,
    remaining_life: life,
    can_attack: false,
    is_attacking: false,
    has_script: info.has_script,
    flavor: info.flavor,
    rules_text: info.rules_text,
    image_url: url,
    // In-flight admin cards may have no faction; "" → "no badge".
    faction: info.faction ?? "",
    race: info.race ?? null,
    class: info.class ?? null,
    expansion_set: info.expansion_set,
    rarity: info.rarity,
    summon_only: false,
    seal_counters: 0,
    face_down: false,
    keywords: [],
  };
}

function jobCardKey(job: AdminJob): string | null {
  // Text-gen and image-gen jobs identify their card via `card_id` +
  // `pool`, not `id` + `pool` (which was the convention for the old
  // `:generate_card` kind). Either shape can land here, so try both.
  if (
    job.kind === "generate_card_text" ||
    job.kind === "generate_card_image"
  ) {
    return null;
  }
  const pool = job.args?.pool;
  const id = job.args?.id;
  if (typeof pool !== "string" || typeof id !== "string") return null;
  return `${pool}:${id}`;
}

interface PerCardJobState {
  regenImage: AdminJob | null;
}

function indexJobsByCard(jobs: AdminJob[]): Map<string, PerCardJobState> {
  const map = new Map<string, PerCardJobState>();
  // jobs is newest-first; first hit per (card, kind) is most recent.
  for (const j of jobs) {
    const key = jobCardKey(j);
    if (!key) continue;
    const cur = map.get(key) ?? { regenImage: null };
    if (j.kind === "regen_image" && cur.regenImage == null) cur.regenImage = j;
    map.set(key, cur);
  }
  return map;
}

function isPending(job: AdminJob | null): boolean {
  return job != null && (job.status === "queued" || job.status === "running");
}

// ---- CARDS tab ----

function AdminCardsTab() {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const { narrow } = useResponsiveLayout();
  const [cards, setCards] = useState<AdminCardInfo[]>([]);
  const [pools, setPools] = useState<string[]>([]);
  const poolCodec = useMemo(() => stringCodec<AdminPool | "all">("all"), []);
  const [poolFilter, setPoolFilter] = useUrlSyncedState<AdminPool | "all">(
    "pool",
    "all",
    true,
    poolCodec,
  );
  const claimCodec = useMemo(() => stringCodec<"all" | "unclaimed">("all"), []);
  const [claimFilter, setClaimFilter] = useUrlSyncedState<"all" | "unclaimed">(
    "claim",
    "all",
    true,
    claimCodec,
  );
  const sortCodec = useMemo(() => stringCodec<AdminCardSort>("cost"), []);
  const [sortMode, setSortMode] = useUrlSyncedState<AdminCardSort>(
    "sort",
    "cost",
    true,
    sortCodec,
  );
  const [selected, setSelected] = useState<AdminCardInfo | null>(null);
  const [detail, setDetail] = useState<AdminCardDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalCards, setTotalCards] = useState(0);
  const [browserFilters, setBrowserFilters] = useState<CardBrowserFilters>({
    q: "",
    factions: ["dwarves", "elves", "goblins", "humans", "orcs"],
    type: "all",
  });
  const [jobs, setJobs] = useState<AdminJob[]>([]);
  const [imageVersions, setImageVersions] = useState<Record<string, number>>(
    {},
  );

  // Detects queued/running → succeeded transitions across polls.
  const prevJobStatus = useRef<Map<string, AdminJobStatus>>(new Map());
  const onBrowserFiltersChange = useCallback((next: CardBrowserFilters) => {
    setBrowserFilters((prev) =>
      prev.q === next.q &&
      prev.type === next.type &&
      prev.factions.join(",") === next.factions.join(",")
        ? prev
        : next,
    );
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { cards, pools, total } = await api.listAdminCards({
        page,
        per_page: 50,
        pool: poolFilter,
        claim: claimFilter,
        sort: sortMode,
        q: browserFilters.q,
        factions: browserFilters.factions.join(","),
        type: browserFilters.type,
      });
      setCards(cards);
      setPools(pools);
      setTotalCards(total);
    } catch (err) {
      if (err instanceof api.ApiError && err.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [navigate, page, poolFilter, claimFilter, sortMode, browserFilters]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [poolFilter, claimFilter, sortMode, browserFilters]);

  const pollJobs = useCallback(async () => {
    try {
      const { jobs } = await api.listAdminJobs();
      setJobs(jobs);
    } catch {}
  }, []);

  useEffect(() => {
    void pollJobs();
    const id = setInterval(pollJobs, 2000);
    return () => clearInterval(id);
  }, [pollJobs]);

  // On regen-image success, bump cache-bust. On any completion of the
  // selected card, re-fetch its detail.
  useEffect(() => {
    const pool = selected?.pool;
    const id = selected?.id;
    let refreshedDetailForSelected = false;

    for (const j of jobs) {
      const key = jobCardKey(j);
      if (!key) continue;
      const trackKey = `${key}:${j.kind}:${j.id}`;
      const prev = prevJobStatus.current.get(trackKey);
      prevJobStatus.current.set(trackKey, j.status);
      const justFinished =
        prev !== j.status &&
        (j.status === "succeeded" || j.status === "failed");
      if (!justFinished) continue;

      if (j.kind === "regen_image" && j.status === "succeeded") {
        setImageVersions((v) => ({ ...v, [key]: (v[key] ?? 0) + 1 }));
      }

      if (
        pool &&
        id &&
        key === `${pool}:${id}` &&
        !refreshedDetailForSelected
      ) {
        refreshedDetailForSelected = true;
        void api
          .getAdminCard(id)
          .then(setDetail)
          .catch(() => {
            /* inspector stays on stale detail; user can re-select */
          });
      }
    }
  }, [jobs, selected]);

  const onSelect = useCallback(async (c: AdminCardInfo) => {
    setSelected(c);
    setDetail(null);
    try {
      const d = await api.getAdminCard(c.id);
      setDetail(d);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const jobsByCard = useMemo(() => indexJobsByCard(jobs), [jobs]);

  // Adapter so CardBrowser can render admin rows. id is `pool:id`
  // because the same card id can appear in multiple pools.
  const browserCards: CatalogCard[] = useMemo(
    () => cards.map(adminInfoToCatalog),
    [cards],
  );
  const adminById = useMemo(() => {
    const m = new Map<string, AdminCardInfo>();
    for (const c of cards) m.set(`${c.pool}:${c.id}`, c);
    return m;
  }, [cards]);
  const compareAdminCards = useCallback(
    (a: CatalogCard, b: CatalogCard) => {
      if (sortMode === "created") {
        const aInfo = adminById.get(a.id);
        const bInfo = adminById.get(b.id);
        const aTime = Date.parse(aInfo?.created_at ?? "");
        const bTime = Date.parse(bInfo?.created_at ?? "");
        const aSort = Number.isFinite(aTime) ? aTime : 0;
        const bSort = Number.isFinite(bTime) ? bTime : 0;
        if (aSort !== bSort) return bSort - aSort;
      } else if (a.cost !== b.cost) {
        return a.cost - b.cost;
      }
      return a.name.localeCompare(b.name);
    },
    [adminById, sortMode],
  );

  const selectedJobs: PerCardJobState = (selected &&
    jobsByCard.get(`${selected.pool}:${selected.id}`)) ?? {
    regenImage: null,
  };

  return (
    // flex:1 + minHeight:0 lets the inner overflow:auto actually scroll.
    // At narrow widths the inspector stacks below the browser and the whole
    // tab becomes a single vertical scroll instead of two columns.
    <div
      style={{
        display: "grid",
        gridTemplateColumns: narrow
          ? "minmax(0, 1fr)"
          : "minmax(360px, 1fr) minmax(420px, 560px)",
        gap: 16,
        flex: 1,
        minHeight: 0,
        overflowY: narrow ? "auto" : undefined,
      }}
    >
      <div style={{ display: "grid", gap: 10, minWidth: 0, minHeight: 0 }}>
        {error && (
          <Banner kind="error" theme={theme}>
            {error}
          </Banner>
        )}
        {loading && cards.length === 0 ? (
          <div style={{ color: theme.inkFaint }}>Loading…</div>
        ) : (
          <>
            <CardBrowser
              cards={browserCards}
              theme={theme}
              urlSync
              serverFiltered
              onFiltersChange={onBrowserFiltersChange}
              selectedId={selected ? `${selected.pool}:${selected.id}` : null}
              onInspect={(c) => {
                const info = adminById.get(c.id);
                if (info) void onSelect(info);
              }}
              badgeFor={(c) => {
                const info = adminById.get(c.id);
                if (!info) return undefined;
                const j = jobsByCard.get(`${info.pool}:${info.id}`);
                if (isPending(j?.regenImage ?? null)) return "IMG…";
                return poolBadge(info.pool);
              }}
              compareCards={compareAdminCards}
              imageVersionFor={(c) => {
                const info = adminById.get(c.id);
                if (!info) return 0;
                return imageVersions[`${info.pool}:${info.id}`] ?? 0;
              }}
              extraFilterControl={
                <>
                  <CardBrowserSelect
                    theme={theme}
                    label="Pool"
                    value={poolFilter}
                    onChange={(v) => setPoolFilter(v as AdminPool | "all")}
                    options={[
                      ["all", "All pools"] as [string, string],
                      ...pools.map(
                        (p) => [p, prettyPoolName(p)] as [string, string],
                      ),
                    ]}
                  />
                  <CardBrowserSelect
                    theme={theme}
                    label="Sort"
                    value={sortMode}
                    onChange={(v) => setSortMode(v as AdminCardSort)}
                    options={[
                      ["cost", "Cost"],
                      ["created", "Newest"],
                    ]}
                  />
                  <CardBrowserSelect
                    theme={theme}
                    label="Claim"
                    value={claimFilter}
                    onChange={(v) => setClaimFilter(v as "all" | "unclaimed")}
                    options={[
                      ["all", "All cards"],
                      ["unclaimed", "Unclaimed only"],
                    ]}
                  />
                </>
              }
            />
            <PaginationControls
              page={page}
              perPage={50}
              total={totalCards}
              theme={theme}
              onPage={setPage}
            />
          </>
        )}
      </div>
      <AdminCardInspector
        info={selected}
        detail={detail}
        imageVersion={
          selected ? (imageVersions[`${selected.pool}:${selected.id}`] ?? 0) : 0
        }
        perCardJobs={selectedJobs}
        onChange={setDetail}
        onReload={load}
        onRefreshJobs={pollJobs}
        onError={setError}
        theme={theme}
      />
    </div>
  );
}

// CatalogCard for an admin row. id is `pool:id` to keep multi-pool
// duplicates distinct in the grid + lookup map.
export function adminInfoToCatalog(info: AdminCardInfo): CatalogCard {
  const rawUrl = typeof info.image_url === "string" ? info.image_url : null;
  return {
    id: `${info.pool}:${info.id}`,
    name: info.name ?? info.id,
    type: (info.type ?? "creature") as CatalogCard["type"],
    cost: info.cost ?? 0,
    attack: info.attack,
    life: info.life,
    faction: (info.faction ?? "").toLowerCase(),
    race: info.race ?? null,
    class: info.class ?? null,
    expansion_set: info.expansion_set,
    pool: info.pool,
    rarity: info.rarity ?? "common",
    rules_text: info.rules_text,
    flavor: info.flavor,
    copy_cap: 2,
    image_url: rawUrl,
  };
}

function poolBadge(pool: AdminPool): string {
  if (pool === "base_set") return "BASE";
  if (pool === "npc_cards") return "NPC";
  if (pool === "main") return "MAIN";
  // Take first ~5 chars of the set name, uppercased.
  return pool.slice(0, 5).toUpperCase();
}

export function prettyPoolName(pool: string): string {
  if (pool === "base_set") return "Base set";
  return pool
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function AdminCardInspector({
  info,
  detail,
  imageVersion,
  perCardJobs,
  onChange,
  onReload,
  onRefreshJobs,
  onError,
  theme,
}: {
  info: AdminCardInfo | null;
  detail: AdminCardDetail | null;
  imageVersion: number;
  perCardJobs: PerCardJobState;
  onChange: (d: AdminCardDetail | null) => void;
  onReload: () => void;
  onRefreshJobs: () => void;
  onError: (msg: string | null) => void;
  theme: typeof pixelTheme;
}) {
  const [jsonText, setJsonText] = useState("");
  const [luaText, setLuaText] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [panelReport, setPanelReport] = useState<api.EvalCardReport | null>(
    null,
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (detail) {
      setJsonText(detail.json_text);
      setLuaText(detail.lua_text ?? "");
      setJsonError(null);
      // Stale eval-card report from a previous card selection shouldn't
      // bleed into the new one.
      setPanelReport(null);
    } else {
      setJsonText("");
      setLuaText("");
      setPanelReport(null);
    }
  }, [detail]);

  if (!info) {
    return (
      <div
        style={{
          padding: 16,
          border: `2px solid ${theme.panelEdge}`,
          background: theme.panel,
          boxShadow: bevelShadow,
          color: theme.inkFaint,
        }}
      >
        Select a card to inspect.
      </div>
    );
  }

  if (!detail) {
    return (
      <div
        style={{
          padding: 16,
          border: `2px solid ${theme.panelEdge}`,
          background: theme.panel,
          boxShadow: bevelShadow,
          color: theme.inkFaint,
        }}
      >
        Loading {info.id}…
      </div>
    );
  }

  function validateJson(text: string): string | null {
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed !== "object" || parsed === null) {
        return "JSON must be an object.";
      }
      if (parsed.id !== info!.id) {
        return `"id" must equal "${info!.id}".`;
      }
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : "Invalid JSON.";
    }
  }

  async function onSave() {
    const err = validateJson(jsonText);
    setJsonError(err);
    if (err) return;
    setBusy("save");
    onError(null);
    try {
      await api.saveAdminCard(info!.id, {
        json: jsonText,
        lua: luaText.trim() === "" ? null : luaText,
      });
      const fresh = await api.getAdminCard(info!.id);
      onChange(fresh);
      onReload();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function onRegenImage() {
    setBusy("regen");
    onError(null);
    try {
      await api.enqueueRegenImage(info!.id);
      onRefreshJobs();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function onCheckBalance() {
    setBusy("check-balance");
    onError(null);
    try {
      // eval-card is a synchronous shellout (block-and-wait). Phoenix
      // returns the parsed JSON report directly.
      const report = await api.evalCardBalance(info!.id);
      setPanelReport(report);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function onGrant() {
    setBusy("grant");
    onError(null);
    try {
      await api.grantToAll(info!.id);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function onDelete() {
    const proceed = window.confirm(
      `Delete "${info!.name ?? info!.id}"?\n\n` +
        "This removes the card directory from disk AND wipes every " +
        "DB row that references it (claims, collections, deck_cards). " +
        "Irreversible.",
    );
    if (!proceed) return;

    setBusy("delete");
    onError(null);
    try {
      const r = await api.deleteAdminCard(info!.id);
      // Drop the inspector's cached detail so the right panel falls
      // back to "Select a card to inspect." after the row vanishes.
      onChange(null);
      onReload();
      // Surface the cascade counts so the admin sees what was cleaned
      // up. window.alert is intentionally synchronous so the message
      // doesn't get drowned out by the immediate grid refresh.
      const { claims, collections, deck_cards, shop_rotations } = r.counts;
      window.alert(
        `Deleted ${info!.id}.\n\n` +
          `Cleaned up ${claims} claim(s), ${collections} collection ` +
          `row(s), ${deck_cards} deck row(s), ${shop_rotations} shop ` +
          `rotation row(s).`,
      );
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      style={{
        display: "grid",
        gap: 12,
        padding: 14,
        border: `2px solid ${theme.panelEdge}`,
        background: theme.panel,
        boxShadow: bevelShadow,
        height: "100%",
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18 }}>{info.name ?? info.id}</h2>
        <span
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            color: theme.inkDim,
          }}
        >
          {info.pool.toUpperCase()} · {info.id}
        </span>
      </div>

      <div
        style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}
      >
        <div style={{ width: naturalCardWidthPx("hero") }}>
          <Card
            card={infoToCardView(info, imageVersion)}
            size="hero"
            theme={theme}
          />
        </div>
      </div>

      <ActionRow>
        <SmallButton
          onClick={() => navigate(`/admin/card-editor/${info.id}/edit`)}
          disabled={false}
          theme={theme}
        >
          EDIT
        </SmallButton>
        <SmallButton
          onClick={onRegenImage}
          disabled={busy !== null || isPending(perCardJobs.regenImage)}
          theme={theme}
        >
          {isPending(perCardJobs.regenImage) ? (
            <AnimatedEllipsis label="GENERATING" />
          ) : busy === "regen" ? (
            "…"
          ) : (
            "REGEN IMAGE"
          )}
        </SmallButton>
        <SmallButton
          onClick={onCheckBalance}
          disabled={busy !== null}
          theme={theme}
        >
          {busy === "check-balance" ? (
            <AnimatedEllipsis label="CHECKING" />
          ) : (
            "CHECK BALANCE"
          )}
        </SmallButton>
        <SmallButton onClick={onGrant} disabled={busy !== null} theme={theme}>
          {busy === "grant" ? "…" : "GRANT 1"}
        </SmallButton>
        <SmallButton
          onClick={onDelete}
          disabled={busy !== null}
          theme={theme}
          danger
        >
          {busy === "delete" ? "DELETING…" : "DELETE CARD"}
        </SmallButton>
      </ActionRow>

      <JobStatusNote
        job={perCardJobs.regenImage}
        label="Image regen"
        theme={theme}
      />

      <AdjBalancePanel
        report={panelReport}
        running={busy === "check-balance"}
        theme={theme}
      />

      <div>
        <Label theme={theme}>CARD.JSON</Label>
        <textarea
          value={jsonText}
          onChange={(e) => {
            setJsonText(e.target.value);
            setJsonError(validateJson(e.target.value));
          }}
          spellCheck={false}
          style={editorStyle(theme, 240)}
        />
        {jsonError && (
          <div style={{ color: theme.accent, fontSize: 12 }}>{jsonError}</div>
        )}
      </div>

      <div>
        <Label theme={theme}>CARD.LUA (empty deletes)</Label>
        <textarea
          value={luaText}
          onChange={(e) => setLuaText(e.target.value)}
          spellCheck={false}
          style={editorStyle(theme, 180)}
        />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <SmallButton
          onClick={onSave}
          disabled={busy !== null || jsonError != null}
          theme={theme}
          primary
        >
          {busy === "save" ? "SAVING…" : "SAVE"}
        </SmallButton>
      </div>
    </div>
  );
}

function AdjBalancePanel({
  report,
  running,
  theme,
}: {
  report: api.EvalCardReport | null;
  running: boolean;
  theme: typeof pixelTheme;
}) {
  if (running) {
    return (
      <div
        style={{
          padding: 10,
          background: theme.bgDeep,
          border: `1px solid ${theme.panelEdge}`,
          color: theme.inkFaint,
          fontSize: 13,
        }}
      >
        <AnimatedEllipsis label="Checking faction-adjusted balance" />
      </div>
    );
  }
  if (!report) return null;

  // adj_pp: the card's win rate vs the fixed 50% target, in percentage
  // points.
  //   • |adj| ≤ 2 → neutral ink (balanced)
  //   • |adj| > 5 → rust (out of band, flagged by the generator gate)
  //   • else      → green/rust depending on sign
  const adj = report.adj_pp;
  const adjColor =
    Math.abs(adj) <= 2
      ? theme.ink
      : Math.abs(adj) > 5
        ? E32.rust
        : adj < 0
          ? E32.rust
          : E32.forest;

  return (
    <div
      style={{
        padding: 10,
        background: theme.bgDeep,
        border: `1px solid ${theme.panelEdge}`,
        display: "grid",
        gap: 6,
      }}
    >
      <Label theme={theme}>BALANCE</Label>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontSize: 22, fontWeight: 700, color: adjColor }}>
          {`${adj >= 0 ? "+" : ""}${adj.toFixed(1)}pp`}
        </span>
        <span style={{ color: theme.inkFaint, fontSize: 12 }}>
          vs {(report.baseline_win_rate * 100).toFixed(0)}% without card
        </span>
      </div>
      <div style={{ color: theme.inkFaint, fontSize: 11 }}>
        win {(report.win_rate * 100).toFixed(1)}% · {report.games} games
        {report.elapsed_secs > 0 && <> · {report.elapsed_secs.toFixed(1)}s</>}
      </div>
    </div>
  );
}

function JobStatusNote({
  job,
  label,
  theme,
}: {
  job: AdminJob | null;
  label: string;
  theme: typeof pixelTheme;
}) {
  if (!job) return null;
  if (job.status === "queued" || job.status === "running") {
    return (
      <div style={{ color: theme.inkFaint, fontSize: 12 }}>
        <AnimatedEllipsis label={`${label} ${job.status}`} />
      </div>
    );
  }
  if (job.status === "failed") {
    return (
      <div
        style={{
          padding: 8,
          background: theme.bgDeep,
          border: `1px solid ${theme.accent}`,
          color: theme.accent,
          fontSize: 12,
          whiteSpace: "pre-wrap",
          maxHeight: 180,
          overflow: "auto",
        }}
      >
        <strong>{label} failed:</strong> {job.error ?? "unknown error"}
      </div>
    );
  }
  return null;
}

// ---- GENERATE tab ----

// "fill" lets the server pick the axis value with the fewest cards;
// "random" lets the sampler pick.
type CardTypeChoice = "fill" | "random" | GenerateCardType;
type FactionChoice = "fill" | "random" | GenerateFaction;
type RarityChoice = "fill" | "random" | GenerateRarity;

export function AdminGenerateTab() {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<AdminJob[]>([]);
  const [cards, setCards] = useState<Map<string, AdminCardInfo>>(new Map());
  const [inspect, setInspect] = useState<CatalogCard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Defaults are RANDOM across all three axes — the no-knobs-touched
  // path produces a fully random card. FILL (lowest-count cell) is
  // still available as an explicit choice for tuning gaps.
  const cardTypeCodec = useMemo(
    () => stringCodec<CardTypeChoice>("random"),
    [],
  );
  const factionCodec = useMemo(() => stringCodec<FactionChoice>("random"), []);
  const rarityCodec = useMemo(() => stringCodec<RarityChoice>("random"), []);
  const [cardType, setCardType] = useUrlSyncedState<CardTypeChoice>(
    "type",
    "random",
    true,
    cardTypeCodec,
  );
  const [faction, setFaction] = useUrlSyncedState<FactionChoice>(
    "faction",
    "random",
    true,
    factionCodec,
  );
  const [rarity, setRarity] = useUrlSyncedState<RarityChoice>(
    "rarity",
    "random",
    true,
    rarityCodec,
  );

  const loadCards = useCallback(async () => {
    try {
      const { cards } = await api.listAdminCards();
      setCards(new Map(cards.map((c) => [c.id, c])));
    } catch {
      // Roster refresh is best-effort; the jobs poll surfaces errors.
    }
  }, []);

  // Succeeded job ids we've already seen, so the jobs poll can tell a
  // *new* success apart from history and refresh the card roster only
  // then (instead of re-fetching the full roster every 2s — that scan
  // is what melted prod's shared CPU).
  const seenSucceededJobsRef = useRef<Set<string> | null>(null);

  const load = useCallback(async () => {
    try {
      const { jobs } = await api.listAdminJobs();
      // Each logical "card generation" is now two jobs (text → image),
      // both surfaced in the Generate tab so the admin can see image
      // promotion separately. `regen_image` is kept off this list (it
      // shows on each card row) the same way it always was.
      const genJobs = jobs.filter(
        (j) =>
          j.kind === "generate_card_text" || j.kind === "generate_card_image",
      );
      setJobs(genJobs);

      const succeeded = genJobs
        .filter((j) => j.status === "succeeded")
        .map((j) => j.id);
      const seen = seenSucceededJobsRef.current;
      if (seen === null) {
        // First poll: history only, no refresh needed (mount fetched).
        seenSucceededJobsRef.current = new Set(succeeded);
      } else if (succeeded.some((id) => !seen.has(id))) {
        succeeded.forEach((id) => seen.add(id));
        void loadCards();
      }
    } catch (err) {
      if (err instanceof api.ApiError && err.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [navigate, loadCards]);

  const onInspectSavedId = useCallback(
    async (id: string) => {
      const info = cards.get(id);
      if (info) {
        setInspect(adminInfoToCatalog(info));
        return;
      }
      // Not in the roster summary — which is only ever a cache that can
      // be stale or still loading, never the source of truth. Fetch the
      // single card directly so a saved card is ALWAYS inspectable and we
      // never block a click behind the full-roster scan.
      try {
        setInspect(await api.getAdminCardCatalog(id));
      } catch {
        setError(`Card "${id}" couldn't be loaded — it may still be promoting.`);
      }
    },
    [cards],
  );

  useEffect(() => {
    void load();
    void loadCards();
    const jobsId = setInterval(load, 2000);
    // Slow safety-net poll for the roster; the jobs poll refreshes it
    // eagerly whenever a generation succeeds.
    const cardsId = setInterval(loadCards, 15000);
    return () => {
      clearInterval(jobsId);
      clearInterval(cardsId);
    };
  }, [load, loadCards]);

  async function onGenerate() {
    setBusy(true);
    setError(null);
    try {
      await api.enqueueGenerate(cardType, faction, rarity);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    // Single scroll container; the pivot table is tall enough that
    // separate scrollboxes would strand the jobs list off-screen.
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        maxWidth: 900,
        flex: 1,
        minHeight: 0,
      }}
    >
      <GenerateSettingsBar onError={setError} />

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <select
          value={cardType}
          onChange={(e) => setCardType(e.target.value as CardTypeChoice)}
          disabled={busy}
          style={selectStyle(theme)}
        >
          <option value="fill">FILL TYPE</option>
          <option value="random">RANDOM TYPE</option>
          <option value="creature">CREATURE</option>
          <option value="spell">SPELL</option>
          <option value="enchantment">ENCHANTMENT</option>
          <option value="trap">TRAP</option>
        </select>
        <select
          value={faction}
          onChange={(e) => setFaction(e.target.value as FactionChoice)}
          disabled={busy}
          style={selectStyle(theme)}
        >
          <option value="fill">FILL FACTION</option>
          <option value="random">RANDOM FACTION</option>
          <option value="humans">HUMANS</option>
          <option value="goblins">GOBLINS</option>
          <option value="dwarves">DWARVES</option>
          <option value="orcs">ORCS</option>
          <option value="elves">ELVES</option>
        </select>
        <select
          value={rarity}
          onChange={(e) => setRarity(e.target.value as RarityChoice)}
          disabled={busy}
          style={selectStyle(theme)}
        >
          <option value="fill">FILL RARITY</option>
          <option value="random">RANDOM RARITY</option>
          <option value="common">COMMON</option>
          <option value="uncommon">UNCOMMON</option>
          <option value="rare">RARE</option>
          <option value="legendary">LEGENDARY</option>
        </select>
        <button
          onClick={onGenerate}
          disabled={busy}
          style={{
            ...ghostButtonStyle(theme),
            background: theme.accent,
            color: theme.bgDeep,
            borderColor: theme.accent,
          }}
        >
          {busy ? "ENQUEUING…" : "GENERATE CARD"}
        </button>
        <span style={{ color: theme.inkFaint, fontSize: 14 }}>
          Click multiple times to queue multiple jobs. Jobs run one at a time.
        </span>
      </div>

      {error && (
        <Banner kind="error" theme={theme}>
          {error}
        </Banner>
      )}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          paddingRight: 4,
        }}
      >
        <AutoGenControlPanel theme={theme} />
        <PacksReadyPanel theme={theme} />
        <PoolStatsPanel theme={theme} />
        <Label theme={theme}>GENERATION JOBS</Label>
        <JobsList
          jobs={jobs}
          theme={theme}
          onInspect={onInspectSavedId}
          readyIds={new Set(cards.keys())}
        />
      </div>
      <Inspector
        card={inspect}
        theme={theme}
        onClose={() => setInspect(null)}
      />
    </div>
  );
}

export function JobsList({
  jobs,
  theme,
  onInspect,
  readyIds,
}: {
  jobs: AdminJob[];
  theme: typeof pixelTheme;
  onInspect: (id: string) => void;
  /** Card ids currently in the admin roster. Saved ids render as
   *  inspect links only once they're here — before that the card is
   *  still staged under cards/pending/ waiting on its image job, and
   *  an inspect lookup would miss. */
  readyIds: Set<string>;
}) {
  if (jobs.length === 0) {
    return <div style={{ color: theme.inkFaint }}>No jobs yet.</div>;
  }
  const imageStatus = latestImageJobStatusByCardId(jobs);
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {jobs.map((j) => (
        <JobRow
          key={j.id}
          job={j}
          theme={theme}
          onInspect={onInspect}
          readyIds={readyIds}
          imageStatus={imageStatus}
        />
      ))}
    </div>
  );
}

/** Latest image-job status per card id. Jobs arrive newest-first from
 *  the API, so the first hit per card wins. */
export function latestImageJobStatusByCardId(
  jobs: AdminJob[],
): Map<string, AdminJobStatus> {
  const out = new Map<string, AdminJobStatus>();
  for (const j of jobs) {
    if (j.kind !== "generate_card_image") continue;
    const cardId = j.args?.card_id;
    if (typeof cardId !== "string" || cardId === "" || out.has(cardId)) {
      continue;
    }
    out.set(cardId, j.status);
  }
  return out;
}

/** How a job row should present one saved-card id. */
export type SavedCardRefState = "ready" | "rendering" | "art_failed" | "missing";

export function savedCardRefState(
  inRoster: boolean,
  imageJobStatus: AdminJobStatus | undefined,
): SavedCardRefState {
  if (inRoster) return "ready";
  if (imageJobStatus === "failed") return "art_failed";
  if (imageJobStatus === "queued" || imageJobStatus === "running") {
    return "rendering";
  }
  return "missing";
}

/** One card id inside a job row. ALWAYS an inspect link — clicking
 *  fetches the card directly, so a saved card is never un-clickable just
 *  because the roster summary cache hasn't caught up. The art-pipeline
 *  state (rendering / failed) is shown as a non-blocking hint only. */
function SavedCardRef({
  id,
  state,
  theme,
  onInspect,
}: {
  id: string;
  state: SavedCardRefState;
  theme: typeof pixelTheme;
  onInspect: (id: string) => void;
}) {
  const note =
    state === "art_failed"
      ? "art failed"
      : state === "rendering"
        ? "rendering art…"
        : null;
  return (
    <>
      <button
        type="button"
        onClick={() => onInspect(id)}
        title="Inspect this card"
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          margin: 0,
          cursor: "pointer",
          color: theme.accent,
          fontFamily: "inherit",
          fontSize: "inherit",
          textDecoration: "underline",
          textUnderlineOffset: 2,
        }}
      >
        <code style={{ color: theme.accent }}>{id}</code>
      </button>
      {note && <span style={{ color: theme.inkFaint }}> ({note})</span>}
    </>
  );
}

function JobRow({
  job,
  theme,
  onInspect,
  readyIds,
  imageStatus,
}: {
  job: AdminJob;
  theme: typeof pixelTheme;
  onInspect: (id: string) => void;
  readyIds: Set<string>;
  imageStatus: Map<string, AdminJobStatus>;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasError = Boolean(job.error);
  const savedIds = job.result?.saved_ids ?? [];
  // Image jobs carry their card id in args — surface it so the admin
  // can tell which card a text→image pair belongs to.
  const imageCardId =
    job.kind === "generate_card_image" && typeof job.args?.card_id === "string"
      ? job.args.card_id
      : null;
  const timings = job.result?.timings ?? [];
  const usage = job.result?.usage ?? [];
  const timingsByCardId: Record<string, AdminJobStageTimings> = {};
  for (const t of timings) {
    timingsByCardId[t.card_id] = t;
  }
  const usageByCardId: Record<string, AdminJobUsage> = {};
  for (const u of usage) {
    usageByCardId[u.card_id] = u;
  }
  const costs = job.result?.costs ?? [];
  const costByCardId: Record<string, AdminJobCardCost> = {};
  for (const c of costs) {
    costByCardId[c.card_id] = c;
  }
  const totalCostUsd = costs.reduce<number>((acc, c) => {
    return acc + (typeof c.total_usd === "number" ? c.total_usd : 0);
  }, 0);

  return (
    <div
      style={{
        border: `2px solid ${theme.panelEdge}`,
        background: theme.panel,
        boxShadow: bevelShadow,
        padding: 10,
        display: "grid",
        gap: 6,
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 13 }}
      >
        <span
          style={{
            color: statusColor(theme, job.status),
            fontFamily: theme.fontDisplay,
            fontSize: 10,
          }}
        >
          {job.status.toUpperCase()}
        </span>
        {job.args?.source === "auto" && (
          <span
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 10,
              color: theme.accent,
            }}
          >
            [AUTO]
          </span>
        )}
        <span
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 10,
            color: theme.inkDim,
          }}
        >
          {job.kind}
        </span>
        <span style={{ color: theme.inkFaint, marginLeft: "auto" }}>
          {fmtDateTime(job.started_at)} → {fmtDateTime(job.finished_at)}
        </span>
        {hasError && (
          <button
            onClick={() => setExpanded((x) => !x)}
            style={ghostButtonStyle(theme)}
          >
            {expanded ? "HIDE ERROR" : "SHOW ERROR"}
          </button>
        )}
      </div>
      {savedIds.length > 0 && (
        <div style={{ fontSize: 12, color: theme.ink }}>
          <span style={{ color: theme.inkDim, marginRight: 6 }}>saved:</span>
          {savedIds.map((id, i) => (
            <span key={id}>
              <SavedCardRef
                id={id}
                state={savedCardRefState(readyIds.has(id), imageStatus.get(id))}
                theme={theme}
                onInspect={onInspect}
              />
              {i < savedIds.length - 1 ? ", " : ""}
            </span>
          ))}
        </div>
      )}
      {imageCardId && (
        <div style={{ fontSize: 12, color: theme.ink }}>
          <span style={{ color: theme.inkDim, marginRight: 6 }}>card:</span>
          <SavedCardRef
            id={imageCardId}
            state={savedCardRefState(readyIds.has(imageCardId), job.status)}
            theme={theme}
            onInspect={onInspect}
          />
        </div>
      )}
      {timings.length > 0 && (
        <div
          style={{ display: "grid", gap: 2, fontSize: 11, color: theme.inkDim }}
        >
          {savedIds.map((id) => {
            const t = timingsByCardId[id];
            if (!t) return null;
            return (
              <div key={id}>
                <code style={{ color: theme.accent, marginRight: 6 }}>
                  {id}
                </code>
                <span>
                  llm{" "}
                  <strong style={{ color: theme.ink }}>
                    {t.llm.toFixed(1)}s
                  </strong>
                  {" · "}
                  validation{" "}
                  <strong style={{ color: theme.ink }}>
                    {t.validation.toFixed(1)}s
                  </strong>
                  {" · "}
                  image{" "}
                  <strong style={{ color: theme.ink }}>
                    {t.image.toFixed(1)}s
                  </strong>
                  {" · "}
                  balance{" "}
                  <strong style={{ color: theme.ink }}>
                    {t.balance.toFixed(1)}s
                  </strong>
                  {" · "}
                  total{" "}
                  <strong style={{ color: theme.ink }}>
                    {t.total.toFixed(1)}s
                  </strong>
                  {t.typicality != null && (
                    <>
                      {" · "}
                      typicality{" "}
                      <strong style={{ color: theme.ink }}>
                        {t.typicality}/10
                      </strong>
                    </>
                  )}
                  {t.seed_words.length > 0 && (
                    <>
                      {" · "}
                      seeds{" "}
                      <strong style={{ color: theme.ink }}>
                        {t.seed_words.join(", ")}
                      </strong>
                    </>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {costs.length > 0 && (
        <div
          style={{ display: "grid", gap: 2, fontSize: 11, color: theme.inkDim }}
        >
          {savedIds.map((id) => {
            const c = costByCardId[id];
            if (!c) return null;
            return (
              <div
                key={`cost-${id}`}
                style={{ display: "flex", alignItems: "baseline", gap: 8 }}
              >
                <span
                  style={{
                    fontFamily: theme.fontDisplay,
                    fontSize: 9,
                    letterSpacing: theme.letterSpacing,
                    textTransform: "uppercase",
                    color: theme.inkDim,
                    flexShrink: 0,
                  }}
                >
                  COST
                </span>
                <span>
                  llm{" "}
                  <strong style={{ color: theme.ink }}>
                    {c.llm_usd != null ? formatUsd(c.llm_usd) : "—"}
                  </strong>
                  {" · "}
                  validation{" "}
                  <strong style={{ color: theme.ink }}>
                    {c.validation_usd != null
                      ? formatUsd(c.validation_usd)
                      : "—"}
                  </strong>
                  {" · "}
                  image{" "}
                  <strong style={{ color: theme.ink }}>
                    {c.image_usd != null ? formatUsd(c.image_usd) : "—"}
                  </strong>
                  {" · "}
                  total{" "}
                  <strong style={{ color: theme.ink }}>
                    {c.total_usd != null ? formatUsd(c.total_usd) : "—"}
                  </strong>
                  {c.model && (
                    <span style={{ color: theme.inkFaint }}> · {c.model}</span>
                  )}
                </span>
              </div>
            );
          })}
          {savedIds.length > 1 && totalCostUsd > 0 && (
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span
                style={{
                  fontFamily: theme.fontDisplay,
                  fontSize: 9,
                  letterSpacing: theme.letterSpacing,
                  textTransform: "uppercase",
                  color: theme.inkDim,
                  flexShrink: 0,
                }}
              >
                TOTAL
              </span>
              <span>
                <strong style={{ color: theme.ink }}>
                  {formatUsd(totalCostUsd)}
                </strong>
              </span>
            </div>
          )}
        </div>
      )}
      {usage.length > 0 && (
        <div
          style={{ display: "grid", gap: 2, fontSize: 11, color: theme.inkDim }}
        >
          {savedIds.map((id) => {
            const u = usageByCardId[id];
            if (!u) return null;
            // hit = cache_read / (input + cache_read + cache_write).
            // Including cache_write avoids over-crediting cold runs.
            const denom =
              u.input_tokens +
              u.cache_read_input_tokens +
              u.cache_creation_input_tokens;
            const hit =
              denom > 0 ? (u.cache_read_input_tokens / denom) * 100 : 0;
            return (
              <div
                key={`usage-${id}`}
                style={{ display: "flex", alignItems: "baseline", gap: 8 }}
              >
                <span
                  style={{
                    fontFamily: theme.fontDisplay,
                    fontSize: 9,
                    letterSpacing: theme.letterSpacing,
                    textTransform: "uppercase",
                    color: theme.inkDim,
                    flexShrink: 0,
                  }}
                >
                  LLM
                </span>
                <span>
                  in{" "}
                  <strong style={{ color: theme.ink }}>
                    {u.input_tokens.toLocaleString()}
                  </strong>
                  {" · "}
                  out{" "}
                  <strong style={{ color: theme.ink }}>
                    {u.output_tokens.toLocaleString()}
                  </strong>
                  {" · "}
                  cache_read{" "}
                  <strong style={{ color: theme.ink }}>
                    {u.cache_read_input_tokens.toLocaleString()}
                  </strong>
                  {" · "}
                  cache_write{" "}
                  <strong style={{ color: theme.ink }}>
                    {u.cache_creation_input_tokens.toLocaleString()}
                  </strong>
                  {denom > 0 && (
                    <>
                      {" · "}
                      hit{" "}
                      <strong style={{ color: theme.ink }}>
                        {hit.toFixed(1)}%
                      </strong>
                    </>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {hasError && expanded && (
        <pre
          style={{
            margin: 0,
            padding: 8,
            background: theme.bgDeep,
            border: `1px solid ${theme.panelEdge}`,
            color: theme.accent,
            fontSize: 11,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: 260,
            overflow: "auto",
          }}
        >
          {job.error}
        </pre>
      )}
    </div>
  );
}

// ---- packs-ready / auto-gen ----

// The auto-gen controls (enable toggle, settings, tick) live in their OWN
// panel that depends ONLY on the fast, DB-backed /auto-gen endpoint — never
// on the card-pool scan behind packs-ready / pool-stats. The enable/disable
// toggle and SETTINGS button render immediately and unconditionally so an
// admin can ALWAYS stop the generator; nothing about them "loads in" behind
// a slow scan. Status/spend/provider detail fill in when available.
function AutoGenControlPanel({ theme }: { theme: typeof pixelTheme }) {
  const [auto, setAuto] = useState<api.AdminAutoGenStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [toggling, setToggling] = useState(false);

  const load = useCallback(async () => {
    try {
      setAuto(await api.getAdminAutoGen());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  async function toggleEnabled() {
    if (!auto) return;
    setToggling(true);
    try {
      setAuto(await api.updateAdminAutoGen({ enabled: !auto.enabled }));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setToggling(false);
    }
  }

  const enabled = auto?.enabled ?? null;
  const enabledLabel = enabled === null ? "…" : enabled ? "ON" : "OFF";

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <Label theme={theme}>AUTO-GEN</Label>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={() => void toggleEnabled()}
            disabled={!auto || toggling}
            style={{
              ...ghostButtonStyle(theme),
              color: enabled ? theme.accent : theme.inkFaint,
            }}
            title="Enable or disable the background card auto-generator"
          >
            AUTO-GEN: {enabledLabel}
          </button>
          <button
            onClick={async () => {
              try {
                await api.tickAdminAutoGen();
                await load();
              } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
              }
            }}
            style={ghostButtonStyle(theme)}
            title="Force one Auto-gen tick now (instead of waiting for the timer)."
          >
            TICK NOW
          </button>
          <button
            onClick={() => setEditing(true)}
            disabled={!auto}
            style={ghostButtonStyle(theme)}
          >
            SETTINGS
          </button>
        </div>
      </div>
      {error && (
        <div style={{ color: E32.amber, fontSize: 11 }}>
          auto-gen status unavailable: {error}
        </div>
      )}
      {auto && <AutoGenStatusLine status={auto} theme={theme} />}
      {auto && (
        <ProviderControlPills status={auto} theme={theme} onChanged={load} />
      )}
      {editing && auto && (
        <AutoGenSettingsModal
          status={auto}
          theme={theme}
          onClose={() => setEditing(false)}
          onSaved={(next) => {
            setAuto(next);
            setEditing(false);
          }}
        />
      )}
    </div>
  );
}

// Pack-readiness tiles. Fed by the slow card-pool scan, so it loads
// independently with its own inline spinner — it must never gate the
// auto-gen controls above it.
function PacksReadyPanel({ theme }: { theme: typeof pixelTheme }) {
  const [data, setData] = useState<{
    packs: api.AdminPackReady[];
    target: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const ready = await api.getAdminPacksReady();
      setData({ packs: ready.pack_types, target: ready.target });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
    // Pack readiness only changes when cards/claims change; 15s keeps
    // the panel honest without hammering the card scan.
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <Label theme={theme}>PACKS READY</Label>
      {error ? (
        <Banner kind="error" theme={theme}>
          {error}
        </Banner>
      ) : !data ? (
        <div style={{ color: theme.inkFaint }}>Loading pack readiness…</div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: 12,
          }}
        >
          {data.packs.map((p) => (
            <PackReadyTile key={p.item_id} pack={p} theme={theme} />
          ))}
        </div>
      )}
    </div>
  );
}

function AutoGenStatusLine({
  status,
  theme,
}: {
  status: api.AdminAutoGenStatus;
  theme: typeof pixelTheme;
}) {
  const kind = status.last_action?.kind ?? null;
  const tone =
    kind === "enqueued" || kind === "enqueued_images"
      ? theme.accent
      : kind === "cap_reached" ||
          kind === "queue_busy" ||
          kind === "breaker_open" ||
          kind === "credits_exhausted" ||
          kind === "backlog_full" ||
          kind === "images_pending"
        ? E32.amber
        : theme.inkFaint;

  const action = status.last_action ?? null;

  // Each action kind carries its own diagnostic payload. Surface enough
  // detail so the admin can tell whether Auto-Gen sat out a tick
  // because the breaker tripped, the spend cap was reached, the queue
  // is full, or nothing needs work.
  let detail = "";
  if (kind === "enqueued" && action) {
    const count = Number(action.count ?? 0);
    const items = Array.isArray(action.items) ? action.items : [];
    const first = items[0] as
      | { pack_item_id?: string; rarity?: string; faction?: string }
      | undefined;
    detail = first
      ? ` ×${count} (first pack=${String(first.pack_item_id ?? "?")} ` +
        `rarity=${String(first.rarity ?? "?")} ` +
        `faction=${String(first.faction ?? "?")})`
      : ` ×${count}`;
  } else if (kind === "enqueued_images" && action) {
    detail = ` ×${Number(action.count ?? 0)} (backfilling card art for staged cards)`;
  } else if (kind === "images_pending" && action) {
    detail =
      ` (${Number(action.count ?? 0)} staged card(s) still need art; ` +
      `new-card gen paused)`;
  } else if (kind === "breaker_open" && action) {
    detail = ` (provider=${String(action.provider ?? "?")})`;
  } else if (kind === "credits_exhausted" && action) {
    detail =
      ` (provider=${String(action.provider ?? "?")} is out of credits — ` +
      `top up, then RESET the provider pill)`;
  } else if (kind === "backlog_full" && action) {
    detail = ` (limit=${String(action.limit ?? "?")})`;
  } else if (kind === "disabled") {
    detail = " (toggle Enabled in SETTINGS)";
  }

  return (
    <div style={{ fontSize: 11, color: theme.inkFaint }}>
      <span>
        Last tick: {fmtDateTime(status.last_tick_at)} —{" "}
        <span style={{ color: tone }}>{(kind ?? "never").toUpperCase()}</span>
        {detail}
      </span>
    </div>
  );
}

/** Visual state for one provider pill. Out-of-credits wins over a
 *  blown breaker — both block dispatch, but the fix is different
 *  (top up + RESET vs wait/RESET), so the label must say which. */
export function providerPillState(
  snap?: api.ProviderControlSnapshot,
): "ok" | "blown" | "no_credits" {
  if (snap?.credits_exhausted) return "no_credits";
  if (snap?.breaker === "open") return "blown";
  return "ok";
}

function ProviderControlPills({
  status,
  theme,
  onChanged,
}: {
  status: api.AdminAutoGenStatus;
  theme: typeof pixelTheme;
  onChanged: () => void | Promise<void>;
}) {
  // The autogen status payload already embeds each provider's
  // ProviderControl snapshot — no separate fetch needed for the
  // happy path. The three pills sit next to each other and only
  // distinguish themselves by name + state.
  const items: {
    id: string;
    label: string;
    snap?: api.ProviderControlSnapshot;
  }[] = [
    { id: "openrouter", label: "OPENROUTER", snap: status.openrouter.provider },
    { id: "pixellab", label: "PIXELLAB", snap: status.pixellab.provider },
    {
      id: "claude_code",
      label: "CLAUDE CODE",
      snap: status.claude_code?.provider,
    },
  ];

  async function reset(id: string) {
    try {
      await api.resetAdminProviderBreaker(id);
      await onChanged();
    } catch {
      // Best-effort UI; the 2s status poll will catch up.
    }
  }

  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {items.map(({ id, label, snap }) => {
        const pillState = providerPillState(snap);
        const blown = pillState === "blown";
        const noCredits = pillState === "no_credits";
        const cap = snap?.effective_cap ?? 0;
        const ceiling = snap?.ceiling ?? 0;
        const tone =
          blown || noCredits
            ? theme.accent
            : cap === 0
              ? E32.amber
              : theme.ink;

        const title = snap
          ? `effective_cap=${cap} / ceiling=${ceiling}` +
            (noCredits
              ? " · out of credits — top up, then press RESET"
              : "") +
            (snap.backoff_remaining_ms > 0
              ? ` · backoff ${Math.ceil(snap.backoff_remaining_ms / 1000)}s`
              : "") +
            (snap.last_failure_reason
              ? ` · last err: ${snap.last_failure_reason}`
              : "")
          : "";

        return (
          <div
            key={id}
            title={title}
            style={{
              border: `2px solid ${theme.panelEdge}`,
              background: theme.panel,
              boxShadow: bevelShadow,
              padding: "4px 8px",
              display: "flex",
              gap: 8,
              alignItems: "center",
              fontFamily: theme.fontDisplay,
              fontSize: 10,
              letterSpacing: theme.letterSpacing,
              color: tone,
            }}
          >
            <span>{label}</span>
            <span style={{ color: theme.inkFaint }}>
              {cap}/{ceiling}
            </span>
            <span
              style={{
                color: blown || noCredits ? theme.accent : theme.inkFaint,
              }}
            >
              {noCredits ? "NO CREDITS" : blown ? "BLOWN" : "OK"}
            </span>
            {(blown || noCredits) && (
              <button
                onClick={() => void reset(id)}
                style={{ ...ghostButtonStyle(theme), padding: "2px 6px" }}
              >
                RESET
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PackReadyTile({
  pack,
  theme,
}: {
  pack: api.AdminPackReady;
  theme: typeof pixelTheme;
}) {
  const tone =
    pack.status === "blocked"
      ? theme.accent
      : pack.status === "below_target"
        ? E32.amber
        : theme.ink;

  return (
    <div
      style={{
        border: `2px solid ${theme.panelEdge}`,
        background: theme.panel,
        boxShadow: bevelShadow,
        padding: 10,
        display: "flex",
        gap: 10,
        alignItems: "center",
      }}
    >
      <ItemSprite id={pack.item_id} size={48} />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 10,
            letterSpacing: theme.letterSpacing,
            color: theme.ink,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {pack.name}
        </div>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 18,
            color: tone,
            lineHeight: 1,
            marginTop: 2,
          }}
          title={`${pack.packs_ready} / ${pack.target} target`}
        >
          {pack.packs_ready}
          <span style={{ fontSize: 10, color: theme.inkFaint }}>
            {" "}
            / {pack.target}
          </span>
        </div>
        <div
          style={{
            fontSize: 10,
            color: theme.inkFaint,
            marginTop: 3,
          }}
        >
          C{pack.commons} · U{pack.uncommons} · R{pack.rares} · L
          {pack.legendaries}
        </div>
      </div>
    </div>
  );
}

function AutoGenSettingsModal({
  status,
  theme,
  onClose,
  onSaved,
}: {
  status: api.AdminAutoGenStatus;
  theme: typeof pixelTheme;
  onClose: () => void;
  onSaved: (next: api.AdminAutoGenStatus) => void;
}) {
  const [enabled, setEnabled] = useState(status.enabled);
  const [target, setTarget] = useState(String(status.target_packs_per_type));
  const [openrouterCap, setOpenrouterCap] = useState(status.openrouter.cap_usd);
  const [pixellabCap, setPixellabCap] = useState(status.pixellab.cap_usd);
  const [openrouterConcurrent, setOpenrouterConcurrent] = useState(
    String(status.openrouter.concurrent_ceiling ?? 1),
  );
  const [pixellabConcurrent, setPixellabConcurrent] = useState(
    String(status.pixellab.concurrent_ceiling ?? 1),
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function onSave() {
    setSaving(true);
    setError(null);
    try {
      const parsedTarget = parseInt(target, 10);
      if (!Number.isFinite(parsedTarget) || parsedTarget < 0) {
        throw new Error("Target packs must be zero or a positive integer.");
      }
      const parsedOr = parseInt(openrouterConcurrent, 10);
      if (!Number.isFinite(parsedOr) || parsedOr < 1) {
        throw new Error("OpenRouter concurrency must be at least 1.");
      }
      const parsedPl = parseInt(pixellabConcurrent, 10);
      if (!Number.isFinite(parsedPl) || parsedPl < 1) {
        throw new Error("PixelLab concurrency must be at least 1.");
      }
      const next = await api.updateAdminAutoGen({
        enabled,
        target_packs_per_type: parsedTarget,
        openrouter_cap_usd: openrouterCap,
        pixellab_cap_usd: pixellabCap,
        openrouter_concurrent: parsedOr,
        pixellab_concurrent: parsedPl,
      });
      onSaved(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.panel,
          border: `2px solid ${theme.panelEdge}`,
          boxShadow: bevelShadow,
          padding: 16,
          width: 380,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 12,
            letterSpacing: theme.letterSpacing,
            color: theme.accent,
          }}
        >
          AUTO-GEN SETTINGS
        </div>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: theme.ink,
            fontSize: 12,
          }}
        >
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Enabled
        </label>

        <SettingsField
          label="TARGET PACKS PER TYPE"
          value={target}
          onChange={setTarget}
          theme={theme}
        />

        <SettingsField
          label={`OPENROUTER CAP USD (24h spend: ${formatUsd(
            parseFloat(status.openrouter.spent_usd_24h),
          )})`}
          value={openrouterCap}
          onChange={setOpenrouterCap}
          theme={theme}
        />

        <SettingsField
          label={`PIXELLAB CAP USD (24h spend: ${formatUsd(
            parseFloat(status.pixellab.spent_usd_24h),
          )})`}
          value={pixellabCap}
          onChange={setPixellabCap}
          theme={theme}
        />

        <SettingsField
          label="MAX CONCURRENT OPENROUTER REQUESTS"
          value={openrouterConcurrent}
          onChange={setOpenrouterConcurrent}
          theme={theme}
        />

        <SettingsField
          label="MAX CONCURRENT PIXELLAB REQUESTS"
          value={pixellabConcurrent}
          onChange={setPixellabConcurrent}
          theme={theme}
        />

        {error && (
          <Banner kind="error" theme={theme}>
            {error}
          </Banner>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={ghostButtonStyle(theme)}
          >
            CANCEL
          </button>
          <button
            onClick={() => void onSave()}
            disabled={saving}
            style={{
              ...ghostButtonStyle(theme),
              background: theme.accent,
              color: theme.bgDeep,
              borderColor: theme.accent,
            }}
          >
            {saving ? "SAVING…" : "SAVE"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function SettingsField({
  label,
  value,
  onChange,
  theme,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  theme: typeof pixelTheme;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <Label theme={theme}>{label}</Label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          padding: "8px 10px",
          background: theme.bgDeep,
          color: theme.ink,
          border: `2px solid ${theme.panelEdge}`,
          fontFamily: "monospace",
          fontSize: 12,
        }}
      />
    </div>
  );
}

// ---- promote-all / stats / players ----

function PoolStatsPanel({ theme }: { theme: typeof pixelTheme }) {
  const [stats, setStats] = useState<Record<
    AdminPool,
    api.AdminPoolStats
  > | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { pools } = await api.getAdminCardStats();
      setStats(pools);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <Banner kind="error" theme={theme}>
        {error}
      </Banner>
    );
  }
  if (!stats) {
    return <div style={{ color: theme.inkFaint }}>Loading pool stats…</div>;
  }

  // base_set first, rest alphabetical (matches backend order).
  const order: AdminPool[] = Object.keys(stats).sort((a, b) => {
    if (a === "base_set") return -1;
    if (b === "base_set") return 1;
    return a.localeCompare(b);
  });

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Label theme={theme}>POOL BREAKDOWN</Label>
        <button onClick={() => void load()} style={ghostButtonStyle(theme)}>
          REFRESH
        </button>
      </div>
      <div style={{ display: "grid", gap: 12 }}>
        {order.map((pool) => {
          const s = stats[pool];
          if (!s) return null;
          return (
            <PoolStatsCard key={pool} pool={pool} stats={s} theme={theme} />
          );
        })}
      </div>
    </div>
  );
}

const STATS_FACTIONS = [
  "dwarves",
  "elves",
  "goblins",
  "humans",
  "orcs",
] as const;
const STATS_RARITIES = ["common", "uncommon", "rare", "legendary"] as const;
const STATS_TYPES = ["creature", "spell", "enchantment", "trap"] as const;

function PoolStatsCard({
  pool,
  stats,
  theme,
}: {
  pool: AdminPool;
  stats: api.AdminPoolStats;
  theme: typeof pixelTheme;
}) {
  // Pivot sparse triples → (type, rarity) → faction → count.
  const grid = useMemo(() => {
    const m = new Map<string, Record<string, number>>();
    for (const t of stats.by_type_rarity_faction) {
      const key = `${t.type}|${t.rarity}`;
      const row = m.get(key) ?? {};
      row[t.faction] = (row[t.faction] ?? 0) + t.count;
      m.set(key, row);
    }
    return m;
  }, [stats.by_type_rarity_faction]);

  // Sanity-check column totals — surfaces "this faction is empty".
  const factionTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const t of stats.by_type_rarity_faction) {
      totals[t.faction] = (totals[t.faction] ?? 0) + t.count;
    }
    return totals;
  }, [stats.by_type_rarity_faction]);

  return (
    <div
      style={{
        border: `2px solid ${theme.panelEdge}`,
        background: theme.panel,
        boxShadow: bevelShadow,
        padding: 10,
        display: "grid",
        gap: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            color: theme.accent,
          }}
        >
          {poolBadge(pool)}
        </span>
        <span
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 14,
            color: theme.ink,
          }}
        >
          {stats.total}
        </span>
      </div>

      {stats.total === 0 ? (
        <div style={{ color: theme.inkFaint, fontSize: 11 }}>(empty pool)</div>
      ) : (
        <PivotTable grid={grid} factionTotals={factionTotals} theme={theme} />
      )}

      {/* Cost is orthogonal to (type, rarity, faction); keep it as a
          one-line summary below the table. */}
      <BucketRow title="Cost" buckets={stats.by_cost} theme={theme} />
    </div>
  );
}

function PivotTable({
  grid,
  factionTotals,
  theme,
}: {
  grid: Map<string, Record<string, number>>;
  factionTotals: Record<string, number>;
  theme: typeof pixelTheme;
}) {
  const headerCellStyle: React.CSSProperties = {
    fontFamily: theme.fontDisplay,
    fontSize: 9,
    letterSpacing: theme.letterSpacing,
    color: theme.inkFaint,
    textAlign: "left",
    padding: "4px 8px",
    borderBottom: `1px solid ${theme.panelEdge}`,
    whiteSpace: "nowrap",
  };
  const rowLabelStyle: React.CSSProperties = {
    fontSize: 11,
    color: theme.ink,
    padding: "3px 8px",
    whiteSpace: "nowrap",
  };
  const numCellBase: React.CSSProperties = {
    fontFamily: theme.fontDisplay,
    fontSize: 11,
    padding: "3px 8px",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead>
          <tr>
            <th style={{ ...headerCellStyle }}>TYPE</th>
            <th style={{ ...headerCellStyle }}>RARITY</th>
            {STATS_FACTIONS.map((f) => (
              <th key={f} style={{ ...headerCellStyle, textAlign: "right" }}>
                {f.toUpperCase()}
              </th>
            ))}
            <th
              style={{
                ...headerCellStyle,
                textAlign: "right",
                color: theme.accent,
              }}
            >
              TOTAL
            </th>
          </tr>
        </thead>
        <tbody>
          {STATS_TYPES.flatMap((t) =>
            STATS_RARITIES.map((r) => {
              const row = grid.get(`${t}|${r}`) ?? {};
              const rowTotal = STATS_FACTIONS.reduce(
                (acc, f) => acc + (row[f] ?? 0),
                0,
              );
              return (
                <tr key={`${t}|${r}`}>
                  <td style={rowLabelStyle}>{t}</td>
                  <td style={{ ...rowLabelStyle, color: theme.inkDim }}>{r}</td>
                  {STATS_FACTIONS.map((f) => {
                    const n = row[f] ?? 0;
                    return (
                      <td
                        key={f}
                        style={{
                          ...numCellBase,
                          color: n === 0 ? theme.inkFaint : theme.ink,
                        }}
                      >
                        {n === 0 ? "·" : n}
                      </td>
                    );
                  })}
                  <td
                    style={{
                      ...numCellBase,
                      color: rowTotal === 0 ? theme.accent : theme.ink,
                    }}
                  >
                    {rowTotal}
                  </td>
                </tr>
              );
            }),
          )}
          <tr>
            <td
              colSpan={2}
              style={{
                ...rowLabelStyle,
                fontFamily: theme.fontDisplay,
                fontSize: 9,
                letterSpacing: theme.letterSpacing,
                color: theme.inkFaint,
                borderTop: `1px solid ${theme.panelEdge}`,
                paddingTop: 6,
              }}
            >
              FACTION TOTAL
            </td>
            {STATS_FACTIONS.map((f) => {
              const n = factionTotals[f] ?? 0;
              return (
                <td
                  key={f}
                  style={{
                    ...numCellBase,
                    borderTop: `1px solid ${theme.panelEdge}`,
                    paddingTop: 6,
                    color: n === 0 ? theme.accent : theme.ink,
                  }}
                >
                  {n}
                </td>
              );
            })}
            <td
              style={{
                ...numCellBase,
                borderTop: `1px solid ${theme.panelEdge}`,
                paddingTop: 6,
                color: theme.accent,
              }}
            >
              {Object.values(factionTotals).reduce((a, b) => a + b, 0)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function BucketRow({
  title,
  buckets,
  theme,
}: {
  title: string;
  buckets: api.AdminPoolBucket[];
  theme: typeof pixelTheme;
}) {
  return (
    <div style={{ fontSize: 11, color: theme.inkDim }}>
      <span
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 9,
          letterSpacing: theme.letterSpacing,
          color: theme.inkFaint,
          marginRight: 6,
        }}
      >
        {title.toUpperCase()}
      </span>
      {buckets.length === 0 ? (
        <span style={{ color: theme.inkFaint }}>—</span>
      ) : (
        buckets.map((b, i) => (
          <span key={b.key}>
            <span style={{ color: theme.ink }}>{b.key}</span>{" "}
            <span style={{ color: theme.inkFaint }}>{b.count}</span>
            {i < buckets.length - 1 ? " · " : ""}
          </span>
        ))
      )}
    </div>
  );
}

// ---- TOOLS tab ----
//
// One-shot admin comparison and validation tools.

type ToolKey =
  | "new_card"
  | "new_deck"
  | "model_compare"
  | "prompt_compare"
  | "validator_test"
  | "palette_snap"
  | "image_creator"
  | "playtest"
  | "balance"
  | "deck_balance";

interface ToolDef {
  key: ToolKey;
  label: string;
  blurb: string;
}

const TOOLS: ToolDef[] = [
  {
    key: "new_card",
    label: "+ New Card",
    blurb:
      "Open the Card Editor on a blank card. Use the AI Fill controls there to generate or hand-author a single card into the live pool.",
  },
  {
    key: "new_deck",
    label: "+ New Deck",
    blurb:
      "Generate a themed NPC deck in one shot. Describe the theme, pick a faction and target Bradley-Terry rating, and the tool drafts every card, rules-validates each, then BT-rates the deck against a random cohort and retries until it lands in the target window. Self-contained — promote to attach the deck to an NPC.",
  },
  {
    key: "model_compare",
    label: "Model Compare",
    blurb:
      "Run the same card-generation prompt through several models and view the outputs side-by-side. Outputs never enter the live pool — they're throwaway artifacts for judging model quality.",
  },
  {
    key: "prompt_compare",
    label: "Prompt Compare",
    blurb:
      "Run the same model and spec through several prompt versions (subdirs of tools/cardgen_data/) and view the outputs side-by-side. Use this to confirm a prompt edit is actually an improvement before promoting the new version.",
  },
  {
    key: "validator_test",
    label: "Validator Test",
    blurb:
      "Score a model against the labeled validator-eval dataset. Each call sends the few-shot training set as cache-warm context, then asks the model to classify one held-out card. Use this to compare alternatives to the production Sonnet validator.",
  },
  {
    key: "palette_snap",
    label: "Palette Snap",
    blurb:
      "Upload a PNG and see it side-by-side with its Endesga-32 conversion. Useful for sanity-checking external art before importing it.",
  },
  {
    key: "image_creator",
    label: "Image Creator",
    blurb:
      "Describe an image and pick a size; PixelLab returns a grid of Endesga-32-snapped variations (size decides how many). Download whichever you want from each run. Throwaway — nothing enters the card pool.",
  },
  {
    key: "playtest",
    label: "Playtest",
    blurb:
      "Replay scenario templates and verify rules fixes. The engine drives canned starting positions deterministically so regressions show up the same way every time.",
  },
  {
    key: "balance",
    label: "Balance",
    blurb:
      "Evolve baseline decks per faction via a GA tournament, then evaluate candidate cards and decks against the active baseline panel.",
  },
  {
    key: "deck_balance",
    label: "Deck Balance",
    blurb:
      "Bradley-Terry ratings for every NPC deck, starter deck, and a seeded cohort of random decks — all rated together by a round-robin. Surfaces how authored decks stack up against the random baseline and each other.",
  },
];

// New Card tool — the Card Editor rendered inline. `useParams` reads
// empty values inside the tools route, which puts the editor in
// blank-new-card mode (same code path the old /admin/card-editor/new
// URL took). Save/cancel navigation inside the editor still works:
// it routes to /admin/cards on save and back to /admin/tools on
// cancel (`returnTo` is unset in this entry point).
function NewCardTool() {
  return <CardEditorPage embedded />;
}

function AdminToolsTab({ initialTool }: { initialTool?: string }) {
  const theme = pixelTheme;
  const { narrow } = useResponsiveLayout();
  // Legacy /admin/playtest and /admin/balance URLs route here with
  // the old tab name in `initialTool`; map them onto the matching
  // sub-tool so bookmarks keep working.
  const resolveInitial = useCallback((raw?: string): ToolKey => {
    switch (raw) {
      case "playtest":
        return "playtest";
      case "balance":
        return "balance";
      case "deck_balance":
        return "deck_balance";
      case "new_card":
      case "new_deck":
      case "model_compare":
      case "prompt_compare":
      case "validator_test":
      case "palette_snap":
      case "image_creator":
        return raw;
      default:
        return "model_compare";
    }
  }, []);
  const [activeTool, setActiveTool] = useState<ToolKey>(() =>
    resolveInitial(initialTool),
  );

  return (
    // Two-pane: tool list left, active tool right. Narrow viewports
    // stack the active tool below the list.
    <div
      style={{
        display: "grid",
        gridTemplateColumns: narrow
          ? "minmax(0, 1fr)"
          : "minmax(280px, 360px) 1fr",
        gap: 16,
        flex: 1,
        minHeight: 0,
        overflowY: narrow ? "auto" : undefined,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        <Label theme={theme}>TOOLS</Label>
        {TOOLS.map((t) => {
          const active = t.key === activeTool;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTool(t.key)}
              style={{
                textAlign: "left",
                padding: 12,
                background: active ? theme.accent : theme.panel,
                color: active ? E32.parchment : theme.ink,
                border: `2px solid ${active ? theme.accent : theme.panelEdge}`,
                boxShadow: bevelShadow,
                cursor: "pointer",
                display: "grid",
                gap: 6,
              }}
            >
              <div
                style={{
                  fontFamily: theme.fontDisplay,
                  fontSize: 10,
                  letterSpacing: theme.letterSpacing,
                }}
              >
                {t.label.toUpperCase()}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: active ? E32.parchment : theme.inkFaint,
                  lineHeight: 1.3,
                }}
              >
                {t.blurb}
              </div>
            </button>
          );
        })}
      </div>
      {/* Flex column + bounded height so a tool whose root is
          `flex:1; minHeight:0; overflowY:auto` (Image Creator, the
          playtest/balance panels) actually scrolls instead of being
          clipped by this wrapper's `overflow: hidden`. */}
      <div
        style={{
          minHeight: 0,
          overflow: "hidden",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {activeTool === "new_card" && <NewCardTool />}
        {activeTool === "new_deck" && <BuildDeckAi />}
        {activeTool === "model_compare" && <ModelCompareTool />}
        {activeTool === "prompt_compare" && <PromptCompareTool />}
        {activeTool === "validator_test" && <ValidatorTestTool />}
        {activeTool === "palette_snap" && <PaletteSnapTool />}
        {activeTool === "image_creator" && <ImageCreatorTool />}
        {activeTool === "playtest" && <AdminPlaytestTab />}
        {activeTool === "balance" && <BalanceTab />}
        {activeTool === "deck_balance" && <DeckBalanceTab />}
      </div>
    </div>
  );
}

const MANAGE_PAGE_SIZE = 50;

function AdminManageTab() {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const [users, setUsers] = useState<api.AdminUser[]>([]);
  const [items, setItems] = useState<api.AdminItem[]>([]);
  // Grantable-cards list is fetched lazily — the first time a
  // CheatsRowMenu opens its Grant Card submenu. ~hundreds of cards
  // would otherwise bloat every page load.
  const [grantableCards, setGrantableCards] = useState<
    api.AdminGrantableCard[] | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Server-side pagination + username filter. `query` is the raw input;
  // `debouncedQuery` is what actually drives the fetch (250ms after the
  // last keystroke) so typing doesn't fire a request per character.
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  // Open popover: row id + the button element to anchor against.
  const [openCheats, setOpenCheats] = useState<{
    userId: number;
    anchor: HTMLElement;
  } | null>(null);

  // Debounce the search box. Any change snaps back to page 1 in the
  // same tick so the fetch effect runs exactly once.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 250);
    return () => window.clearTimeout(t);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listAdminUsers({
        q: debouncedQuery,
        page,
        pageSize: MANAGE_PAGE_SIZE,
      });
      setUsers(res.users);
      setTotal(res.total);
      setError(null);
    } catch (err) {
      if (err instanceof api.ApiError && err.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [navigate, debouncedQuery, page]);

  useEffect(() => {
    void load();
  }, [load]);

  // The item catalog (for the Grant Item submenu) is small and static
  // across pages — fetch it once on mount.
  useEffect(() => {
    void (async () => {
      try {
        const { items: i } = await api.listAdminItems();
        setItems(i);
      } catch {
        // Non-fatal: the Grant Item submenu just stays empty.
      }
    })();
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / MANAGE_PAGE_SIZE));

  function reportError(err: unknown) {
    setError(err instanceof Error ? err.message : String(err));
  }

  // Patch a single row in place — no full-table refetch. Keeps the
  // popover anchored, doesn't flicker, and avoids re-rendering rows
  // the admin didn't touch.
  function patchUser(userId: number, patch: Partial<api.AdminUser>) {
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, ...patch } : u)),
    );
  }

  async function reset(user: api.AdminUser) {
    const ok = window.confirm(
      `Fully reset ${user.username}'s account?\n\nWipes every owned card, deck, inventory slot, pack, and currency balance, then re-seeds the canonical starter cards + decks. HP back to 100.\n\nThis cannot be undone.`,
    );
    if (!ok) return;
    try {
      await api.resetCollection(user.id);
      setError(null);
      // Reset rewrites enough state that a row-patch isn't enough —
      // refetch from the server.
      await load();
    } catch (err) {
      reportError(err);
    }
  }

  async function ban(user: api.AdminUser) {
    const next = !user.is_banned;
    const ok = window.confirm(
      next
        ? `Ban ${user.username}? They will be logged out of any live session and locked out of every API. They can still log in to see the "You're Banned!" screen.`
        : `Unban ${user.username}? They will regain full access on next login.`,
    );
    if (!ok) return;
    try {
      const resp = await api.banUser(user.id, next);
      patchUser(user.id, { is_banned: resp.is_banned });
      setError(null);
      if (next) playSfx("ai_quill_scratch");
    } catch (err) {
      reportError(err);
    }
  }

  // ---- Cheat action handlers ----
  //
  // Each one calls its endpoint, patches the affected row from the
  // server response, refreshes the admin's own /me if they targeted
  // themselves, and leaves the popover open. Outside-click / Esc /
  // explicit close action dismisses the menu — never an action itself.

  async function doGrantGold(user: api.AdminUser) {
    try {
      const resp = await api.grantCurrency(user.id, "gold", 10000);
      patchUser(user.id, { gold_coins: resp.balances.gold_coins });
      setError(null);
    } catch (err) {
      reportError(err);
    }
  }

  async function doGrantJuice(user: api.AdminUser) {
    try {
      const resp = await api.grantCurrency(user.id, "void_juice", 10000);
      patchUser(user.id, { void_juice_ml: resp.balances.void_juice_ml });
      setError(null);
    } catch (err) {
      reportError(err);
    }
  }

  async function doGrantItem(user: api.AdminUser, itemId: string) {
    try {
      await api.grantItem(user.id, itemId);
      // Item grants don't change any visible row column today
      // (inventory isn't on the table). No row patch needed.
      setError(null);
    } catch (err) {
      reportError(err);
    }
  }

  async function loadGrantableCards() {
    if (grantableCards != null) return;
    try {
      const { cards } = await api.listGrantableCards();
      setGrantableCards(cards);
    } catch (err) {
      reportError(err);
    }
  }

  async function doGrantCard(user: api.AdminUser, cardId: string) {
    try {
      await api.grantCard(user.id, cardId);
      // Mark this card as claimed in our local cache so the submenu
      // greys it out on the next render without a full re-fetch.
      setGrantableCards((cur) =>
        cur == null
          ? cur
          : cur.map((c) =>
              c.id === cardId ? { ...c, claimed_by_user_id: user.id } : c,
            ),
      );
      setError(null);
    } catch (err) {
      reportError(err);
    }
  }

  async function doRestoreHp(user: api.AdminUser) {
    try {
      const resp = await api.restoreHp(user.id);
      patchUser(user.id, { current_hp: resp.current_hp });
      setError(null);
    } catch (err) {
      reportError(err);
    }
  }

  async function doSetUnlock(
    user: api.AdminUser,
    faction: "orcs" | "elves",
    unlocked: boolean,
  ) {
    try {
      const resp = await api.setFactionUnlock(user.id, faction, unlocked);
      patchUser(user.id, {
        faction_unlocks: {
          ...user.faction_unlocks,
          [resp.faction]: resp.unlocked,
        },
      });
      setError(null);
    } catch (err) {
      reportError(err);
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
      }}
    >
      {error && (
        <Banner kind="error" theme={theme}>
          {error}
        </Banner>
      )}
      <CheatsPanel theme={theme} onError={setError} />

      {/* Username filter — server-side, so it scales to any user count. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by username…"
          aria-label="Filter users by username"
          style={{
            flex: "1 1 240px",
            minWidth: 200,
            padding: "8px 10px",
            fontSize: 13,
            fontFamily: theme.fontBody,
            color: theme.ink,
            background: theme.bg,
            border: `2px solid ${theme.panelEdge}`,
            boxShadow: bevelShadow,
          }}
        />
        {query && (
          <button onClick={() => setQuery("")} style={ghostButtonStyle(theme)}>
            CLEAR
          </button>
        )}
        <span
          style={{
            color: theme.inkFaint,
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
          }}
        >
          {total.toLocaleString()} {total === 1 ? "USER" : "USERS"}
        </span>
      </div>

      {loading ? (
        <div style={{ color: theme.inkFaint }}>Loading…</div>
      ) : users.length === 0 ? (
        <div style={{ color: theme.inkFaint }}>
          {debouncedQuery ? "No users match that filter." : "No users."}
        </div>
      ) : (
        // Horizontal scroll on narrow viewports — keep cell content
        // on a single line via `whiteSpace: nowrap` (applied per-Th
        // and per-td below).
        <div
          style={{
            overflow: "auto",
            border: `2px solid ${theme.panelEdge}`,
            background: theme.panel,
            boxShadow: bevelShadow,
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13,
              background: theme.panel,
              whiteSpace: "nowrap",
            }}
          >
            <thead>
              <tr style={{ background: theme.bgDeep }}>
                <Th theme={theme}>USER</Th>
                <Th theme={theme}>CARDS</Th>
                <Th theme={theme}>SLOTS</Th>
                <Th theme={theme}>VOID JUICE</Th>
                <Th theme={theme}>GOLD</Th>
                <Th theme={theme}>HP</Th>
                <Th theme={theme}>BAN</Th>
                <Th theme={theme}>CHEATS</Th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  style={{ borderTop: `1px solid ${theme.panelEdge}` }}
                >
                  <td style={{ padding: 8 }}>
                    <span style={{ color: theme.ink }}>{u.username}</span>
                    {u.is_admin && <BadgeChip theme={theme}>ADMIN</BadgeChip>}
                    {u.is_guest && <BadgeChip theme={theme}>GUEST</BadgeChip>}
                    {u.is_banned && (
                      <BadgeChip theme={theme} tone="alert">
                        BANNED
                      </BadgeChip>
                    )}
                  </td>
                  <td style={{ padding: 8, color: theme.inkDim }}>
                    {u.cards_owned.toLocaleString()}
                  </td>
                  <td style={{ padding: 8, color: theme.inkDim }}>
                    {u.deck_slots}
                  </td>
                  <td style={{ padding: 8, color: theme.inkDim }}>
                    {formatVoidJuice(u.void_juice_ml)}
                  </td>
                  <td style={{ padding: 8, color: theme.inkDim }}>
                    {u.gold_coins.toLocaleString()}
                  </td>
                  <td style={{ padding: 8, color: theme.inkDim }}>
                    {u.current_hp}
                  </td>
                  <td style={{ padding: 8 }}>
                    <button
                      onClick={() => void ban(u)}
                      style={{
                        ...ghostButtonStyle(theme),
                        ...(u.is_banned
                          ? {}
                          : {
                              background: E32.oxblood,
                              color: E32.parchment,
                              borderColor: E32.ruby,
                            }),
                      }}
                    >
                      {u.is_banned ? "UNBAN" : "BAN"}
                    </button>
                  </td>
                  <td style={{ padding: 8 }}>
                    <button
                      onClick={(ev) => {
                        const target = ev.currentTarget;
                        setOpenCheats((cur) =>
                          cur?.userId === u.id
                            ? null
                            : { userId: u.id, anchor: target },
                        );
                      }}
                      style={ghostButtonStyle(theme)}
                    >
                      CHEATS ▾
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination — only meaningful once there's more than one page. */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
          }}
        >
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            style={{
              ...ghostButtonStyle(theme),
              opacity: page <= 1 || loading ? 0.5 : 1,
            }}
          >
            ◂ PREV
          </button>
          <span
            style={{
              color: theme.inkDim,
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              letterSpacing: theme.letterSpacing,
            }}
          >
            PAGE {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
            style={{
              ...ghostButtonStyle(theme),
              opacity: page >= totalPages || loading ? 0.5 : 1,
            }}
          >
            NEXT ▸
          </button>
        </div>
      )}

      {openCheats &&
        (() => {
          const u = users.find((row) => row.id === openCheats.userId);
          if (!u) return null;
          return (
            <CheatsRowMenu
              theme={theme}
              user={u}
              items={items}
              grantableCards={grantableCards}
              loadGrantableCards={() => void loadGrantableCards()}
              anchor={openCheats.anchor}
              onClose={() => setOpenCheats(null)}
              onError={reportError}
              onReset={() => {
                setOpenCheats(null);
                void reset(u);
              }}
              doGrantGold={() => void doGrantGold(u)}
              doGrantJuice={() => void doGrantJuice(u)}
              doGrantItem={(itemId) => void doGrantItem(u, itemId)}
              doGrantCard={(cardId) => void doGrantCard(u, cardId)}
              doRestoreHp={() => void doRestoreHp(u)}
              doSetUnlock={(faction, unlocked) =>
                void doSetUnlock(u, faction, unlocked)
              }
            />
          );
        })()}
    </div>
  );
}

function BadgeChip({
  theme,
  children,
  tone,
}: {
  theme: typeof pixelTheme;
  children: React.ReactNode;
  tone?: "alert";
}) {
  return (
    <span
      style={{
        marginLeft: 6,
        fontFamily: theme.fontDisplay,
        fontSize: 9,
        color: tone === "alert" ? E32.ruby : theme.accent,
      }}
    >
      {children}
    </span>
  );
}

/**
 * Per-row Cheats popover. Portal-rendered (so the table's
 * `overflow: auto` can't clip it) and positioned in viewport space
 * via the anchor button's bounding rect. Actions DO NOT close the
 * menu — the admin can click "+10k Gold" three times, then "Restore
 * HP", without re-opening the menu. Outside click, Esc, or "Reset
 * Account" (destructive) closes it.
 */
function CheatsRowMenu({
  theme,
  user,
  items,
  grantableCards,
  loadGrantableCards,
  anchor,
  onClose,
  onError,
  onReset,
  doGrantGold,
  doGrantJuice,
  doGrantItem,
  doGrantCard,
  doRestoreHp,
  doSetUnlock,
}: {
  theme: typeof pixelTheme;
  user: api.AdminUser;
  items: api.AdminItem[];
  grantableCards: api.AdminGrantableCard[] | null;
  loadGrantableCards: () => void;
  anchor: HTMLElement;
  onClose: () => void;
  onError: (msg: string) => void;
  onReset: () => void;
  doGrantGold: () => void;
  doGrantJuice: () => void;
  doGrantItem: (itemId: string) => void;
  doGrantCard: (cardId: string) => void;
  doRestoreHp: () => void;
  doSetUnlock: (faction: "orcs" | "elves", unlocked: boolean) => void;
}) {
  type Sub = "item" | "card" | "unlock" | null;
  const [sub, setSub] = useState<Sub>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const MENU_WIDTH = 240;

  // Compute viewport-fixed position from the anchor button. Re-read
  // on scroll / resize so the popover stays glued to its button.
  const [pos, setPos] = useState(() => {
    const r = anchor.getBoundingClientRect();
    return { top: r.bottom + 4, left: r.right - MENU_WIDTH };
  });
  useEffect(() => {
    function reposition() {
      const r = anchor.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.right - MENU_WIDTH });
    }
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [anchor]);

  useEffect(() => {
    function onDocClick(ev: MouseEvent) {
      const t = ev.target;
      // Treat the anchor button as part of the popover so clicking it
      // again (to toggle off) routes through the table's own handler
      // instead of through outside-click here.
      if (
        t instanceof Node &&
        rootRef.current &&
        !rootRef.current.contains(t) &&
        !anchor.contains(t)
      ) {
        onClose();
      }
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchor, onClose]);

  return createPortal(
    <div
      ref={rootRef}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        width: MENU_WIDTH,
        zIndex: 1000,
        background: theme.panel,
        border: `2px solid ${theme.panelEdgeStrong}`,
        boxShadow: bevelShadow,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <MenuItem theme={theme} onClick={doGrantGold}>
        + 10,000 Gold
      </MenuItem>
      <MenuItem theme={theme} onClick={doGrantJuice}>
        + 10,000 Void Juice
      </MenuItem>
      <MenuItem
        theme={theme}
        onClick={() => setSub(sub === "item" ? null : "item")}
      >
        Grant Item ▸
      </MenuItem>
      <MenuItem
        theme={theme}
        onClick={() => {
          if (sub === "card") {
            setSub(null);
            return;
          }
          loadGrantableCards();
          setSub("card");
        }}
      >
        Grant Card ▸
      </MenuItem>
      <MenuItem theme={theme} onClick={doRestoreHp}>
        Restore HP (100)
      </MenuItem>
      <MenuItem
        theme={theme}
        onClick={() => setSub(sub === "unlock" ? null : "unlock")}
      >
        Unlock ▸
      </MenuItem>
      <MenuItem theme={theme} tone="destructive" onClick={onReset}>
        Reset Account
      </MenuItem>
      {sub === "item" && (
        <GrantItemSubmenu theme={theme} items={items} onPick={doGrantItem} />
      )}
      {sub === "card" && (
        <GrantCardSubmenu
          theme={theme}
          cards={grantableCards}
          onPick={doGrantCard}
        />
      )}
      {sub === "unlock" && (
        <UnlockSubmenu
          theme={theme}
          user={user}
          onSet={doSetUnlock}
          onError={onError}
        />
      )}
    </div>,
    document.body,
  );
}

function GrantCardSubmenu({
  theme,
  cards,
  onPick,
}: {
  theme: typeof pixelTheme;
  cards: api.AdminGrantableCard[] | null;
  onPick: (cardId: string) => void;
}) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    if (!cards) return [];
    const q = query.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.id.toLowerCase().includes(q) ||
        c.faction.toLowerCase().includes(q) ||
        c.rarity.toLowerCase().includes(q),
    );
  }, [cards, query]);

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        right: "100%",
        marginRight: 4,
        background: theme.panel,
        border: `2px solid ${theme.panelEdgeStrong}`,
        boxShadow: bevelShadow,
        width: 320,
        maxHeight: 420,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <input
        type="text"
        autoFocus
        value={query}
        onChange={(ev) => setQuery(ev.target.value)}
        placeholder="search name / id / faction / rarity…"
        style={{
          fontFamily: theme.fontBody,
          fontSize: 13,
          padding: "8px 10px",
          background: theme.bgDeep,
          color: theme.ink,
          border: "none",
          borderBottom: `2px solid ${theme.panelEdge}`,
          outline: "none",
        }}
      />
      <div style={{ overflowY: "auto", flex: 1 }}>
        {cards == null ? (
          <div style={{ padding: 8, color: theme.inkFaint, fontSize: 12 }}>
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 8, color: theme.inkFaint, fontSize: 12 }}>
            No matches.
          </div>
        ) : (
          filtered.map((c) => {
            const claimed = c.claimed_by_user_id != null;
            return (
              <button
                key={c.id}
                type="button"
                disabled={claimed}
                onClick={() => {
                  if (!claimed) onPick(c.id);
                }}
                title={
                  claimed
                    ? `Already claimed by user #${c.claimed_by_user_id}`
                    : `${c.id} · ${c.faction} · ${c.rarity}`
                }
                style={{
                  textAlign: "left",
                  padding: "6px 10px",
                  background: theme.panel,
                  color: claimed ? theme.inkFaint : theme.ink,
                  border: "none",
                  borderBottom: `1px solid ${theme.panelEdge}`,
                  cursor: claimed ? "not-allowed" : "pointer",
                  fontFamily: theme.fontBody,
                  fontSize: 13,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  opacity: claimed ? 0.55 : 1,
                }}
              >
                <span style={{ flex: 1 }}>{c.name}</span>
                <span
                  style={{
                    fontFamily: theme.fontDisplay,
                    fontSize: 9,
                    color: theme.inkFaint,
                    textTransform: "uppercase",
                  }}
                >
                  {c.faction} · {c.rarity}
                </span>
                {claimed && (
                  <span
                    style={{
                      fontFamily: theme.fontDisplay,
                      fontSize: 9,
                      color: E32.ruby,
                    }}
                  >
                    CLAIMED
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function MenuItem({
  theme,
  children,
  onClick,
  chevron: _chevron,
  tone,
}: {
  theme: typeof pixelTheme;
  children: React.ReactNode;
  onClick: () => void;
  chevron?: boolean;
  tone?: "destructive";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: "left",
        padding: "8px 12px",
        background: theme.panel,
        color: tone === "destructive" ? E32.ruby : theme.ink,
        border: "none",
        borderBottom: `1px solid ${theme.panelEdge}`,
        cursor: "pointer",
        fontFamily: theme.fontBody,
        fontSize: 13,
      }}
    >
      {children}
    </button>
  );
}

function GrantItemSubmenu({
  theme,
  items,
  onPick,
}: {
  theme: typeof pixelTheme;
  items: api.AdminItem[];
  onPick: (itemId: string) => void;
}) {
  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        right: "100%",
        marginRight: 4,
        background: theme.panel,
        border: `2px solid ${theme.panelEdgeStrong}`,
        boxShadow: bevelShadow,
        minWidth: 220,
        maxHeight: 360,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {items.length === 0 ? (
        <div style={{ padding: 8, color: theme.inkFaint, fontSize: 12 }}>
          No items.
        </div>
      ) : (
        items.map((it) => (
          <MenuItem key={it.id} theme={theme} onClick={() => onPick(it.id)}>
            {it.name}
          </MenuItem>
        ))
      )}
    </div>
  );
}

const DEFAULT_UNLOCKED_FACTIONS = ["humans", "goblins", "dwarves"] as const;
const TOGGLEABLE_FACTIONS = ["orcs", "elves"] as const;
const ALL_FACTIONS_FOR_DISPLAY = [
  ...DEFAULT_UNLOCKED_FACTIONS,
  ...TOGGLEABLE_FACTIONS,
] as const;

function UnlockSubmenu({
  theme,
  user,
  onSet,
  onError,
}: {
  theme: typeof pixelTheme;
  user: api.AdminUser;
  onSet: (faction: "orcs" | "elves", unlocked: boolean) => void;
  onError: (msg: string) => void;
}) {
  function toggle(f: "orcs" | "elves") {
    const next = !user.faction_unlocks[f];
    try {
      onSet(f, next);
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        right: "100%",
        marginRight: 4,
        background: theme.panel,
        border: `2px solid ${theme.panelEdgeStrong}`,
        boxShadow: bevelShadow,
        minWidth: 220,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {ALL_FACTIONS_FOR_DISPLAY.map((f) => {
        const isDefault = (
          DEFAULT_UNLOCKED_FACTIONS as readonly string[]
        ).includes(f);
        const checked = isDefault
          ? true
          : user.faction_unlocks[f as "orcs" | "elves"];
        return (
          <button
            key={f}
            type="button"
            disabled={isDefault}
            onClick={() => {
              if (!isDefault) toggle(f as "orcs" | "elves");
            }}
            style={{
              textAlign: "left",
              padding: "8px 12px",
              background: theme.panel,
              color: isDefault ? theme.inkFaint : theme.ink,
              border: "none",
              borderBottom: `1px solid ${theme.panelEdge}`,
              cursor: isDefault ? "default" : "pointer",
              fontFamily: theme.fontBody,
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                background: checked ? theme.accent : theme.panelEdge,
                border: `2px solid ${checked ? theme.accent : theme.inkFaint}`,
              }}
            />
            <span style={{ textTransform: "capitalize" }}>{f}</span>
            {isDefault && (
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 10,
                  color: theme.inkFaint,
                }}
              >
                (default)
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Server-side toggles in `:persistent_term`; affect every pack open
// across all users until the server restarts.
// to land it.
function CheatsPanel({
  theme,
  onError,
}: {
  theme: typeof pixelTheme;
  onError: (msg: string | null) => void;
}) {
  const [cheats, setCheats] = useState<api.AdminCheats | null>(null);
  const [busy, setBusy] = useState<keyof api.AdminCheats | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await api.getAdminCheats();
        if (!cancelled) setCheats(c);
      } catch (err) {
        if (!cancelled)
          onError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onError]);

  async function toggle(key: keyof api.AdminCheats) {
    if (!cheats) return;
    const next = !cheats[key];
    setBusy(key);
    onError(null);
    // Layered on the doc-listener's button click; toggles → quill.
    playSfx("ai_quill_scratch");
    try {
      const updated = await api.updateAdminCheats({ [key]: next });
      setCheats(updated);
    } catch (err) {
      playSfx("ai_error_sound");
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      style={{
        border: `2px solid ${theme.panelEdge}`,
        background: theme.panel,
        boxShadow: bevelShadow,
        padding: 12,
        display: "grid",
        gap: 8,
      }}
    >
      <Label theme={theme}>CHEATS · TESTING ONLY</Label>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <CheatToggle
          theme={theme}
          label="Packs Always Have Legendary"
          on={cheats?.force_legendary ?? false}
          disabled={cheats == null || busy != null}
          onClick={() => void toggle("force_legendary")}
        />
        <CheatToggle
          theme={theme}
          label="Packs Always Have Rare"
          on={cheats?.force_rare ?? false}
          disabled={cheats == null || busy != null}
          onClick={() => void toggle("force_rare")}
        />
        <CheatToggle
          theme={theme}
          label="Admin Mode"
          on={cheats?.admin_mode ?? false}
          disabled={cheats == null || busy != null}
          onClick={() => void toggle("admin_mode")}
        />
      </div>
      <div style={{ color: theme.inkFaint, fontSize: 11 }}>
        Server-side state, in-memory only — resets on server restart. Legendary
        takes precedence over rare when both are on. Admin Mode reveals a SKIP
        (ADMIN) button on the challenge fight modal that credits the win without
        playing the battle.
      </div>
    </div>
  );
}

function CheatToggle({
  theme,
  label,
  on,
  disabled,
  onClick,
}: {
  theme: typeof pixelTheme;
  label: string;
  on: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        fontFamily: theme.fontDisplay,
        fontSize: 10,
        letterSpacing: theme.letterSpacing,
        padding: "8px 14px",
        background: on ? theme.accent : theme.bgDeep,
        color: on ? theme.bgDeep : theme.inkDim,
        border: `2px solid ${on ? theme.accent : theme.panelEdge}`,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <span
        style={{
          width: 10,
          height: 10,
          background: on ? theme.bgDeep : theme.panelEdge,
          border: `2px solid ${on ? theme.bgDeep : theme.inkFaint}`,
        }}
      />
      {label}
    </button>
  );
}

function Th({
  theme,
  children,
}: {
  theme: typeof pixelTheme;
  children: React.ReactNode;
}) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: "8px",
        fontFamily: theme.fontDisplay,
        fontSize: 10,
        letterSpacing: theme.letterSpacing,
        color: theme.inkFaint,
      }}
    >
      {children}
    </th>
  );
}

// ---- styling bits ----

function TabButton({
  active,
  children,
  onClick,
  theme,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
  theme: typeof pixelTheme;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        fontFamily: theme.fontDisplay,
        fontSize: 10,
        padding: "6px 12px",
        background: active ? theme.accent : theme.panel,
        color: active ? theme.bgDeep : theme.inkDim,
        border: `2px solid ${active ? theme.accent : theme.panelEdge}`,
        letterSpacing: theme.letterSpacing,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function SmallButton({
  onClick,
  disabled,
  children,
  theme,
  primary,
  danger,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  theme: typeof pixelTheme;
  primary?: boolean;
  /** Red styling for destructive actions (e.g. DELETE CARD). Wins
   *  over `primary` when both are passed. */
  danger?: boolean;
}) {
  // Theme has no `:danger` token; pull a red from the Endesga32 palette.
  const dangerColor = E32.scarlet;
  const bg = danger ? dangerColor : primary ? theme.accent : theme.panel;
  const fg = danger ? E32.parchment : primary ? theme.bgDeep : theme.inkDim;
  const border = danger
    ? dangerColor
    : primary
      ? theme.accent
      : theme.panelEdge;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        fontFamily: theme.fontDisplay,
        fontSize: 9,
        padding: "6px 10px",
        background: bg,
        color: fg,
        border: `2px solid ${border}`,
        letterSpacing: theme.letterSpacing,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function ActionRow({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{children}</div>
  );
}

export function PaginationControls({
  page,
  perPage,
  total,
  theme,
  onPage,
}: {
  page: number;
  perPage: number;
  total: number;
  theme: typeof pixelTheme;
  onPage: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / perPage));
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
      }}
    >
      <button
        onClick={() => onPage(Math.max(1, page - 1))}
        disabled={page <= 1}
        {...defaultBtn(theme, page <= 1)}
      >
        PREV
      </button>
      <span style={{ color: theme.inkFaint, fontSize: 12 }}>
        Page {page} / {pages} · {total} cards
      </span>
      <button
        onClick={() => onPage(Math.min(pages, page + 1))}
        disabled={page >= pages}
        {...defaultBtn(theme, page >= pages)}
      >
        NEXT
      </button>
    </div>
  );
}

export function Label({
  children,
  theme,
}: {
  children: React.ReactNode;
  theme: typeof pixelTheme;
}) {
  return (
    <div
      style={{
        fontFamily: theme.fontDisplay,
        fontSize: 9,
        letterSpacing: theme.letterSpacing,
        color: theme.inkDim,
        marginBottom: 4,
      }}
    >
      {children}
    </div>
  );
}

function Banner({
  kind,
  children,
  theme,
}: {
  kind: "error";
  children: React.ReactNode;
  theme: typeof pixelTheme;
}) {
  return (
    <div
      style={{
        padding: 10,
        background: theme.panel,
        border: `2px solid ${kind === "error" ? theme.accent : theme.panelEdge}`,
        color: kind === "error" ? theme.accent : theme.ink,
      }}
    >
      {children}
    </div>
  );
}

function editorStyle(
  theme: typeof pixelTheme,
  minHeight: number,
): React.CSSProperties {
  return {
    width: "100%",
    minHeight,
    padding: 8,
    background: theme.bgDeep,
    color: theme.ink,
    border: `1px solid ${theme.panelEdge}`,
    fontFamily: "monospace",
    fontSize: 12,
    resize: "vertical",
  };
}

function selectStyle(theme: typeof pixelTheme): React.CSSProperties {
  return {
    padding: "8px 10px",
    background: theme.panel,
    color: theme.ink,
    border: `2px solid ${theme.panelEdge}`,
    fontFamily: theme.fontDisplay,
    fontSize: 10,
    letterSpacing: theme.letterSpacing,
  };
}

// ----------------------------------------------------------------------
// Playtest tab
// ----------------------------------------------------------------------

function AdminPlaytestTab() {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<
    api.PlaytestTemplateSummary[] | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listPlaytestTemplates()
      .then((res) => {
        if (cancelled) return;
        setTemplates(res.templates);
        setError(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setTemplates([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onPlay = useCallback(
    async (id: string) => {
      setBusyId(id);
      try {
        const res = await api.createPlaytestSession(id);
        playSfx("ai_button_click");
        navigate(`/battle/${res.game_id}`);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusyId(null);
      }
    },
    [navigate],
  );

  if (templates === null) {
    return (
      <div
        style={{
          color: theme.inkDim,
          fontFamily: theme.fontDisplay,
          fontSize: 11,
        }}
      >
        Loading templates…
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {error ? (
        <div
          style={{
            background: theme.panel,
            border: `2px solid ${E32.ruby}`,
            padding: 10,
            color: E32.ruby,
            fontFamily: theme.fontBody,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      ) : null}
      {templates.length === 0 ? (
        <div
          style={{
            color: theme.inkDim,
            fontFamily: theme.fontBody,
            fontSize: 13,
            padding: 12,
          }}
        >
          No playtest templates yet. Drop a JSON file in
          <code style={{ marginLeft: 6 }}>server/priv/playtest_templates/</code>
          and reload.
        </div>
      ) : (
        templates.map((t) => (
          <div
            key={t.id}
            style={{
              background: theme.panel,
              border: `2px solid ${theme.panelEdge}`,
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                minWidth: 0,
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontFamily: theme.fontDisplay,
                  fontSize: 12,
                  letterSpacing: theme.letterSpacing,
                  color: theme.ink,
                }}
              >
                {t.name}
              </h2>
              <span
                style={{
                  fontFamily: theme.fontDisplay,
                  fontSize: 10,
                  color: theme.inkDim,
                }}
              >
                {t.id}
              </span>
            </div>
            <button
              disabled={busyId !== null}
              onClick={() => onPlay(t.id)}
              style={{
                fontFamily: theme.fontDisplay,
                fontSize: 11,
                letterSpacing: theme.letterSpacing,
                padding: "8px 14px",
                background: busyId === t.id ? theme.panelEdge : theme.panel,
                color: theme.ink,
                border: `2px solid ${theme.panelEdge}`,
                cursor: busyId !== null ? "wait" : "pointer",
                flexShrink: 0,
              }}
            >
              {busyId === t.id ? "STARTING…" : "PLAY"}
            </button>
          </div>
        ))
      )}
    </div>
  );
}

function ghostButtonStyle(theme: typeof pixelTheme): React.CSSProperties {
  return {
    fontFamily: theme.fontDisplay,
    fontSize: 10,
    letterSpacing: theme.letterSpacing,
    padding: "6px 10px",
    background: theme.panel,
    color: theme.inkDim,
    border: `2px solid ${theme.panelEdge}`,
    cursor: "pointer",
  };
}

function statusColor(
  theme: typeof pixelTheme,
  status: AdminJob["status"],
): string {
  switch (status) {
    case "queued":
      return theme.inkFaint;
    case "running":
      return theme.accent;
    case "succeeded":
      return theme.ink;
    case "failed":
      return theme.accent;
  }
}

// Date to the left of the time, ISO-style: keeps long-running jobs
// readable when they straddle midnight in the admin's timezone.
function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${d.toLocaleTimeString()}`;
  } catch {
    return iso;
  }
}

/** Format a USD amount for the cost rows. Card-generation costs land
 *  in the cents-to-low-tens-of-cents range; "$0.0123" is more useful
 *  to an admin watching costs than "$0.01" rounded. We render four
 *  fractional digits below $1 and two digits above. */
export function formatUsd(amount: number): string {
  if (!Number.isFinite(amount)) return "—";
  if (Math.abs(amount) >= 1) {
    return `$${amount.toFixed(2)}`;
  }
  return `$${amount.toFixed(4)}`;
}
